import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import dayjs from "dayjs";
import {
  Send,
  Paperclip,
  X,
  ArrowLeft,
  Bot,
  Lock,
  Info,
  AlertCircle,
  Check,
  CheckCheck,
  CheckCircle2,
  Search,
  Zap,
  UserPlus,
  ArrowLeftRight,
  AlertTriangle,
  Clock,
  TimerOff,
  Package,
  FileText,
  FileImage,
  FileAudio,
  FileVideo,
  Mic,
  Users,
  Smartphone,
  Globe,
  MoreHorizontal,
  ChevronDown,
  UserCheck,
  ImageOff,
  Sticker,
  Pencil,
  RotateCcw,
  History,
  Folder,
  FolderInput,
  Tag,
  Plus,
  Bell,
  BellOff,
  Wand2,
  Sparkles,
  Reply,
  Store,
  UserRound,
  Pin,
  PinOff,
} from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { roleRoutes } from "../../utils/roles";
import {
  assignConversation,
  resolveConversation,
  takeoverAiLog,
  postHelpdeskMessage,
  renameConversation,
  isEndpointMissing,
} from "../../services/aiChatService";
import Modal from "../ui/Modal";
import PendingBackendNotice from "../ui/PendingBackendNotice";
import {
  caseClosedLabel,
  caseIndexFor,
  caseIndexKey,
  caseLabel,
  caseReopenLabel,
  caseSeparatorLabel,
  closedByName,
} from "./tickets";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import { Badge } from "../ui/Badge";
import ContactPanel from "./ContactPanel";
import VoiceRecorder from "./VoiceRecorder";
import MediaLightbox from "./MediaLightbox";
import AgentPicker from "./AgentPicker";
import ThreadSearch, { useThreadSearch, THREAD_SEARCH_ID } from "./ThreadSearch";
import QuickReplyPicker from "./QuickReplyPicker";
import "./helpdeskMobile.scss";
import CaseHistoryPopover from "./CaseHistoryPopover";
import FolderPicker from "./FolderPicker";
import ContactAvatar from "./ContactAvatar";
import { AutomationTag, PrivateNoteBubble, isPrivateNote } from "./PrivateNote";
import PrivateNoteComposer from "./PrivateNoteComposer";
import LabelChip from "./LabelChip";
import LabelPicker from "./LabelPicker";
import useHelpdeskLabels from "./useHelpdeskLabels";
import MacrosControl, { MacrosButton } from "./MacrosControl";
import { CopilotMenu, CopilotPanel, useCopilot } from "./Copilot";
import { useDesktopNotificationsPreference } from "./desktopNotificationsPreference";
import { setConversationLabels } from "../../services/helpdeskWorkflowService";
import { toast } from "../../utils/toast";
import { workflowErrorMessage } from "./workflowErrorMessage";
// Nombre visible de una conversación (incluye el canal interno, que no tiene
// contacto). Se reusa para que el header diga lo MISMO que la fila de la lista.
import { conversationDisplayName } from "./ConversationList";
import { FOLDER_COLOR_STYLES, DEFAULT_FOLDER_COLOR } from "./folderColors";
import { extractQuote, previewOfThreadMessage } from "./quotedMessage";
import { withGroupMentionNames } from "./groupMentions";
import { replyRouteNotice } from "./replyRouteNotice";
import { renderWhatsApp } from "../../utils/whatsappFormat";
import { cn } from "../../utils/cn";
import useIsMobile from "../../hooks/useIsMobile";
import { INCIDENT_TYPE_LABEL } from "../../constants/incidents";

// Canales cuyo texto usa el marcado de WhatsApp (*negrita*, _cursiva_, …).
// El portal manda texto plano y la IA ya se renderiza con ReactMarkdown.
const WHATSAPP_CHANNELS = ["whatsapp", "whatsapp_unofficial"];

// Canales sin herramientas de trabajo (notas, etiquetas, macros, copiloto):
// portal y logs de IA viven fuera del microservicio de conversaciones
// omnicanal (otra ruta, ver convBasePath), y el canal interno del equipo queda
// fuera por contrato. Sin canal = portal (misma convención que el routing).
const NO_WORKFLOW_CHANNELS = ["portal", "ai", "internal"];

export function supportsWorkflowTools(conversation) {
  const channel = conversation?.channel;
  return !!channel && !NO_WORKFLOW_CHANNELS.includes(channel);
}

// Distancia (px) al final del hilo dentro de la cual se considera que el agente
// "sigue pegado al fondo". Regla de oro de los chats: si está leyendo historia
// más arriba, un mensaje nuevo NO puede arrastrarlo al final.
const STICK_TO_BOTTOM_PX = 120;

// Límite del nombre de grupo (mismo que el alias de contacto del ContactPanel).
const MAX_GROUP_NAME = 120;

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return !!window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// ─── Helper: avatar del contacto en header del detalle ─────────────────────
function avatarTone(seed) {
  // No brand/warning tones (second visual pass): orange and brown avatars
  // read as accent/notes and, in dark, as the same brown as the agent bubble.
  const tones = [
    "bg-bg-subtle text-fg-muted",
    "bg-status-info-soft text-status-info",
    "bg-status-success-soft text-status-success",
    "bg-status-cc-soft text-status-cc",
  ];
  if (!seed) return tones[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return tones[h % tones.length];
}

// ─── Separador temporal entre mensajes (Hoy / Ayer / fecha) ───────────────
function daySeparatorLabel(date) {
  const d = dayjs(date);
  const now = dayjs();
  if (d.isSame(now, "day")) return "Hoy";
  if (d.isSame(now.subtract(1, "day"), "day")) return "Ayer";
  if (d.isSame(now, "year")) return d.format("ddd D MMM");
  return d.format("ddd D MMM YYYY");
}

// ─── Runs de mensajes consecutivos (estilo WhatsApp) ───────────────────────
// Dos mensajes pertenecen al mismo run si comparten remitente lógico y hay
// <5 min entre ellos dentro del mismo día. Los mensajes system cortan el run
// (y el cambio de día también, lo que cubre el separador de fecha).
function messageRunKey(m, isGroup) {
  if (!m) return null;
  // from_me/source separan los ecos del teléfono/WA Web de lo enviado desde
  // la app: así el badge "Desde el teléfono" conserva su propio encabezado.
  const echo = m.from_me ? `fm:${m.metadata?.source || ""}` : "";
  const participant = isGroup ? m.participant_phone || m.sender_name || "" : "";
  return `${m.sender_role}|${m.sender_id || ""}|${participant}|${echo}`;
}

function isSameRun(a, b, isGroup) {
  if (!a || !b) return false;
  if (a.sender_role === "system" || b.sender_role === "system") return false;
  if (messageRunKey(a, isGroup) !== messageRunKey(b, isGroup)) return false;
  const ta = a.created_at || a.createdAt || null;
  const tb = b.created_at || b.createdAt || null;
  if (!ta || !tb) return false;
  return (
    dayjs(tb).isSame(dayjs(ta), "day") &&
    Math.abs(dayjs(tb).diff(dayjs(ta), "minute")) < 5
  );
}

const MAX_FILE_SIZE_MB = 50;
const ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
  "audio/ogg",
  "audio/mpeg",
  "audio/mp3",
  "audio/mp4",
  "audio/x-m4a",
  "audio/aac",
  "audio/amr",
  "audio/wav",
  "audio/wave",
  "audio/x-wav",
  "audio/webm",
  "audio/opus",
  "video/mp4",
  "video/3gpp",
  "video/quicktime",
  "video/webm",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/zip",
  "application/x-rar-compressed",
  "application/x-7z-compressed",
  "text/plain",
  "text/csv",
];

function isAllowedMime(mime) {
  if (!mime) return true;
  if (ALLOWED_MIME_TYPES.includes(mime)) return true;
  const base = String(mime).split(";")[0].trim().toLowerCase();
  if (ALLOWED_MIME_TYPES.includes(base)) return true;
  return /^(image|audio|video)\//.test(base);
}

function IconForFile({ mime, className = "w-4 h-4" }) {
  if (!mime) return <FileText className={className} aria-hidden="true" />;
  if (mime.startsWith("image/")) return <FileImage className={className} aria-hidden="true" />;
  if (mime.startsWith("audio/")) return <FileAudio className={className} aria-hidden="true" />;
  if (mime.startsWith("video/")) return <FileVideo className={className} aria-hidden="true" />;
  return <FileText className={className} aria-hidden="true" />;
}

const STATUS_LABELS = {
  pending: "Pendiente",
  assigned: "Asignado",
  resolved: "Resuelto",
  ai: "Atendido por IA",
};

// Tono de la pastilla de estado de la cabecera (la única pastilla de esa fila).
const STATUS_TONE = {
  pending: "bg-status-warning-soft text-status-warning",
  assigned: "bg-status-info-soft text-status-info",
  resolved: "bg-status-success-soft text-status-success",
};

// Para el agente el canal operativo es "WhatsApp": la distinción oficial /
// no-oficial (Evolution) es técnica y vive en el tooltip (CHANNEL_TITLES), no
// en una etiqueta que se truncaba a "WA NO ·" y parecía un error de UI.
const CHANNEL_LABELS = {
  portal: "Portal",
  whatsapp: "WhatsApp",
  whatsapp_unofficial: "WhatsApp",
  messenger: "Messenger",
  instagram: "Instagram",
  ai: "IA",
  // Canal interno (asistente de gerencia): sin esta etiqueta, la línea de
  // metadatos del header imprimía el identificador crudo "internal".
  internal: "Interno",
};

const CHANNEL_TITLES = {
  whatsapp_unofficial: "Canal WhatsApp no oficial",
};

// Comercio en la línea de datos del header: texto plano con ícono (second
// visual pass: ya no es una pill). No usa <CommerceBadge/> porque ese
// componente depende de clases `commerce-badge*` inexistentes en el CSS.
function CommerceChip({ name, t }) {
  if (!name) return null;
  return (
    <span
      className="inline-flex items-center gap-[4px] min-w-0 max-w-[180px]"
      title={t("Comercio: {{nombre}}").replace("{{nombre}}", name)}
    >
      <Store className="w-3 h-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{name}</span>
    </span>
  );
}

// ─── Imagen adjunta con estado de error ──────────────────────────────────────
// Hay media histórica cuyo objeto en S3 está corrupto (bytes no decodificables
// con content-type image/jpeg): sin este estado, el <img> roto desparramaba el
// alt por la burbuja. El tile hereda el color de texto de la burbuja (blanco
// en agente, fg en cliente) para funcionar sobre ambos fondos.
function ImageAttachment({ msg, onImageOpen }) {
  const { t } = useTranslation();
  const [broken, setBroken] = useState(false);
  const url = msg.media_url;

  if (broken) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-3 rounded-lg bg-black/10 min-w-[180px]",
          msg.content && "mb-1",
        )}
      >
        <ImageOff className="w-4 h-4 shrink-0 opacity-70" aria-hidden="true" />
        <span className="text-xs opacity-80">{t("Imagen no disponible")}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onImageOpen?.({ url, caption: msg.content || "" })}
      aria-label={msg.content || t("Ver imagen")}
      className={cn(
        "block max-w-[70vw] sm:max-w-[300px] overflow-hidden cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        msg.content ? "rounded-md mb-1" : "rounded-lg",
      )}
    >
      <img
        src={url}
        alt={msg.content || t("Imagen")}
        loading="lazy"
        onError={() => setBroken(true)}
        className="w-full h-auto max-h-[340px] object-cover dark-safe-img"
      />
    </button>
  );
}

// ─── Recibo de entrega estilo WhatsApp ───────────────────────────────────────
// Solo se pinta cuando el backend reporta `delivery_status`. Si viene undefined
// (hoy en prod, y siempre en portal/IA) no se muestra NADA: un check gris
// inventaría un estado que no tenemos.
//
// `surface` dice sobre QUÉ fondo se pinta el recibo, porque ninguna combinación
// de colores sirve para los tres:
//   · "brand" → burbuja naranja del agente. El blanco heredado sobre #F97316 da
//     2.8:1, bajo el 3:1 que WCAG 1.4.11 exige a la iconografía con significado:
//     los checks van en brand-900 (3.3:1) y los estados que deben destacar
//     (leído / fallido) dentro de un chip blanco, donde el contraste que cuenta
//     pasa a ser ícono-vs-chip (>=4.8:1).
//   · "dark"  → pill negra superpuesta a la imagen: ahí el blanco sí contrasta.
//   · "plain" → burbuja clara de IA/portal.
// El estado nunca depende solo del color: la forma cambia (Check → CheckCheck →
// AlertCircle) y "leído" siempre añade un contenedor redondeado, para que no se
// distinga de "entregado" únicamente por el tinte (WCAG 1.4.1).
function DeliveryReceipt({ status, surface = "plain" }) {
  const { t } = useTranslation();
  if (!status) return null;

  const bare = "inline-flex shrink-0";
  const lightChip =
    "inline-flex items-center justify-center rounded-full bg-white px-[3px] py-px shrink-0";

  if (status === "sent" || status === "delivered") {
    const label = status === "sent" ? t("Enviado") : t("Entregado");
    const Glyph = status === "sent" ? Check : CheckCheck;
    return (
      <span role="img" aria-label={label} title={label} className={bare}>
        <Glyph
          className={cn("w-3.5 h-3.5", surface === "brand" && "text-brand-900")}
          aria-hidden="true"
        />
      </span>
    );
  }

  if (status === "read") {
    const label = t("Leído");
    // El chip blanco solo funciona sobre fondos saturados u oscuros; sobre la
    // burbuja clara se usa el tinte info-soft, que da la misma señal de forma.
    return surface === "plain" ? (
      <span
        role="img"
        aria-label={label}
        title={label}
        className="inline-flex items-center justify-center rounded-full bg-status-info-soft px-[3px] py-px shrink-0"
      >
        <CheckCheck className="w-3 h-3 text-status-info" aria-hidden="true" />
      </span>
    ) : (
      <span role="img" aria-label={label} title={label} className={lightChip}>
        <CheckCheck className="w-3 h-3 text-accent-600" aria-hidden="true" />
      </span>
    );
  }

  if (status === "failed") {
    const label = t("No se pudo entregar");
    return surface === "plain" ? (
      <span role="img" aria-label={label} title={label} className={bare}>
        <AlertCircle className="w-3.5 h-3.5 text-status-error" aria-hidden="true" />
      </span>
    ) : (
      <span role="img" aria-label={label} title={label} className={lightChip}>
        {/* Rojo literal, no token: el chip es blanco en ambos temas pero
            --status-error se aclara en dark (#F87171 sobre blanco = 2.8:1).
            Este es el valor de --status-error en light → 4.8:1 sobre el chip. */}
        <AlertCircle className="w-3 h-3 text-[rgb(220_38_38)]" aria-hidden="true" />
      </span>
    );
  }

  return null;
}

// ─── Renderizador de adjuntos en el mensaje ───────────────────────────────────
function MediaRenderer({ msg, onImageOpen }) {
  const { t } = useTranslation();
  const type = msg.content_type;
  const url = msg.media_url;
  if (!url || type === "text") return null;

  if (type === "image") {
    return <ImageAttachment msg={msg} onImageOpen={onImageOpen} />;
  }
  if (type === "audio" || type === "voice_note") {
    return (
      <div className="mt-1 flex items-center gap-2">
        {type === "voice_note" && (
          <Mic className="w-4 h-4 text-fg-muted shrink-0" aria-hidden="true" />
        )}
        <audio controls src={url} preload="metadata" className="max-w-full" />
      </div>
    );
  }
  if (type === "video") {
    return (
      <video
        controls
        src={url}
        preload="metadata"
        className={cn(
          "max-w-[70vw] sm:max-w-[300px] max-h-[340px] rounded-lg border border-border",
          msg.content && "mb-1",
        )}
      />
    );
  }
  if (type === "document") {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-bg-muted/50 text-sm text-fg hover:bg-bg-muted transition-colors"
      >
        <FileText className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="truncate">{msg.media_filename || "Documento"}</span>
      </a>
    );
  }
  if (type === "sticker") {
    // Sin borde ni radio: el sticker de WhatsApp es una imagen con transparencia
    // que va "suelta". La burbuja la pinta el llamador (que la suprime para
    // stickers) y el lightbox NO se abre — un sticker no se amplía.
    return (
      <img
        src={url}
        alt={t("Sticker")}
        loading="lazy"
        draggable={false}
        className="w-28 h-auto max-w-full select-none dark-safe-img"
      />
    );
  }
  return null;
}

// ─── Banner de ventana de mensajería (WA / FB / IG) ───────────────────────────
function WindowBanner({ conversation }) {
  const { t } = useTranslation();
  const channel = conversation?.channel;
  // El canal interno es un hilo del equipo, no una conversación con un contacto:
  // no hay ventana de mensajería que expirar.
  if (
    !channel ||
    channel === "portal" ||
    channel === "whatsapp_unofficial" ||
    channel === "ai" ||
    channel === "internal"
  ) {
    return null;
  }

  const windowExpires = conversation?.messaging_window_expires;
  const windowActive = conversation?.window_active;

  if (windowActive === false || (windowExpires && dayjs().isAfter(dayjs(windowExpires)))) {
    return (
      <div className="px-4 py-2 flex items-center gap-2 text-xs bg-status-error-soft text-status-error border-b border-status-error/20">
        <TimerOff className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span>
          {t("Ventana de {{channel}} expirada.").replace(
            "{{channel}}",
            CHANNEL_LABELS[channel] || channel,
          )}
          {channel === "whatsapp" && " " + t("Solo se pueden enviar Template Messages.")}
          {(channel === "messenger" || channel === "instagram") &&
            " " + t("Se usará el tag human_agent (7 días).")}
        </span>
      </div>
    );
  }

  if (windowExpires) {
    const remaining = dayjs(windowExpires).diff(dayjs(), "hour");
    if (remaining <= 4) {
      return (
        <div className="px-4 py-2 flex items-center gap-2 text-xs bg-status-warning-soft text-status-warning border-b border-status-warning/20">
          <Clock className="w-4 h-4 shrink-0" aria-hidden="true" />
          <span>
            {remaining <= 0
              ? t("La ventana de mensajería expira en menos de 1 hora")
              : t("La ventana de mensajería expira en {{h}}h").replace("{{h}}", remaining)}
          </span>
        </div>
      );
    }
  }
  return null;
}

// ─── Banner contextual de IA ──────────────────────────────────────────────────
// Banner fino de estado de la IA BYO, bajo el header (mismo patrón que
// WindowBanner). Sin botones: escribir (sendAgentReply) o tomar el caso ya
// silencian la IA en el backend. Nivel de módulo — jamás inline (remount).
function ByoAiBanner({ conversation }) {
  const { t } = useTranslation();
  if (!conversation || conversation.status === "resolved") return null;
  if (conversation.ai_escalated) {
    // Quién cortó la conversación automática: el contacto (pidió un humano) o la
    // propia IA (se apartó sola, p. ej. por una consulta que no puede resolver).
    // Son dos hechos distintos para quien toma el caso, así que no se comparte
    // el texto.
    const byAi = conversation.ai_escalated_reason === "ai_derived";
    // Aviso fino teñido de ámbar: pide acción de una persona, así que destaca
    // sin la banda naranja sólida de antes.
    return (
      <div className="px-[16px] py-[6px] flex items-center gap-[8px] text-[12px] leading-[16px] bg-status-warning-soft text-fg border-b border-status-warning/25">
        <UserRound className="w-3.5 h-3.5 shrink-0 text-status-warning" aria-hidden="true" />
        <span>
          {byAi
            ? t("La IA derivó el caso a una persona")
            : t("El contacto pidió hablar con un humano")}
        </span>
      </div>
    );
  }
  if (conversation.byo_ai_active) {
    return (
      <div className="px-[16px] py-[6px] flex items-center gap-[8px] text-[12px] leading-[16px] bg-bg-muted/60 text-fg border-b border-border/60">
        <Bot className="w-3.5 h-3.5 shrink-0 text-status-info" aria-hidden="true" />
        <span>
          {t("La IA está atendiendo esta conversación — escribe o toma el caso para intervenir")}
        </span>
      </div>
    );
  }
  return null;
}

function AIContextBanner({ conversation }) {
  const { t } = useTranslation();
  if (conversation?.channel !== "ai") return null;
  return (
    <div className="mx-[16px] mt-[12px] px-[12px] py-[8px] flex items-start gap-[8px] rounded-lg bg-bg-subtle text-fg text-[12px] leading-[18px]">
      <Bot className="w-4 h-4 shrink-0 mt-[1px] text-fg-muted" aria-hidden="true" />
      <div className="flex-1">
        <div className="font-medium">{t("Conversación atendida por IA")}</div>
        <div className="text-fg-muted mt-[2px]">
          {conversation.was_transferred
            ? t("La IA derivó esta conversación a un agente. Puedes responder al usuario debajo.")
            : t("Puedes intervenir y responder al usuario aunque la IA ya haya contestado.")}
        </div>
      </div>
    </div>
  );
}

// ─── Modal: confirmar cierre ──────────────────────────────────────────────────
function ConfirmResolveModal({ open, onCancel, onConfirm, loading }) {
  const { t } = useTranslation();
  // A6 · Escape + bloqueo del scroll del body (patrón de ui/Modal). El bloqueo
  // no es cosmético: es lo que dispara la red de seguridad del shell
  // (`body[style*="overflow: hidden"] .bottom-nav { pointer-events: none }`)
  // para que la barra inferior no capture toques con el diálogo abierto.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onCancel]);
  if (!open) return null;
  // zIndex 1200 = banda de overlays de src/scss/_bottom-nav.scss:8-26. Con
  // `z-50` el diálogo quedaba bajo la bottom-nav (1000) y bajo el header (150).
  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "var(--overlay-backdrop, rgba(0,0,0,0.5))", zIndex: 1200 }}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="hd-resolve-title"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-xl bg-bg border border-border shadow-xl p-5"
      >
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-status-warning-soft text-status-warning mb-3">
          <AlertTriangle className="w-5 h-5" aria-hidden="true" />
        </div>
        <h3 id="hd-resolve-title" className="text-base font-semibold text-fg">
          {t("Cerrar conversación")}
        </h3>
        <p className="mt-1 text-sm text-fg-muted">
          {t("Esta acción cerrará la conversación y no se podrán enviar más mensajes. ¿Deseas continuar?")}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="h-9 px-3 rounded-md text-sm font-medium text-fg hover:bg-bg-muted transition-colors"
          >
            {t("Cancelar")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium bg-status-error text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {loading && (
              <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
            )}
            {t("Confirmar")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Acciones de IA dentro del hilo ─────────────────────────────────────────
// Mensajes que documentan lo que la IA hizo (o está por hacer) en el caso. Son
// eventos de auditoría dentro de una conversación, no mensajes de nadie: por eso
// se marcan con un badge discreto y NO se reescribe el contenido ni la burbuja.
//
// `ai_note` es una nota interna (solo la ve el equipo: este componente se monta
// únicamente en la bandeja de agentes) y por eso lleva candado.
//
// Exportada para test: decidir qué badge va en qué mensaje es la única lógica
// nueva, y no se justifica renderizar el hilo entero para cubrirla.
export function aiMessageBadge(msg, t) {
  const meta = msg?.metadata || {};
  if (meta.kind === "ai_action_confirm") {
    return { label: t("Esperando confirmación del contacto"), tone: "warning", Icon: Clock };
  }
  if (meta.kind === "ai_action_result") {
    // El backend no garantiza una sola forma de decir "falló": cualquiera de
    // estas señales alcanza. Sin ninguna, la acción se dio por ejecutada.
    const failed =
      meta.ok === false ||
      !!meta.error ||
      !!meta.error_code ||
      meta.status === "failed" ||
      meta.status === "error";
    return failed
      ? { label: t("Falló"), tone: "error", Icon: AlertTriangle }
      : { label: t("Acción ejecutada por IA"), tone: "brand", Icon: Zap };
  }
  if (meta.kind === "ai_note") {
    return { label: t("Nota interna"), tone: "warning", Icon: Lock };
  }
  return null;
}

// Nota de sistema `incident_created`: la deja la vista "Atención e incidencias"
// al registrar una incidencia desde la conversación. El texto se reconstruye en
// el idioma del agente con el catálogo de tipos del front; el `content` que
// persistió el servidor ("Incidencia creada en el pedido N", en español) queda
// como respaldo para cuando el tipo no viene o este bundle todavía no lo conoce.
//
// Exportada para test, igual que aiMessageBadge: es lógica pura y no se
// justifica renderizar el hilo entero para cubrirla.
export function systemNoteLabel(msg, t, getDisplayName) {
  const meta = msg?.metadata;
  if (meta?.kind !== "incident_created") return null;

  // hasOwnProperty y no un acceso directo: el backend solo exige /^[a-z_]+$/,
  // así que "constructor" es un incident_type válido y leería el prototipo.
  const type = meta.incident_type;
  const typeLabel =
    typeof type === "string" && Object.prototype.hasOwnProperty.call(INCIDENT_TYPE_LABEL, type)
      ? t(INCIDENT_TYPE_LABEL[type])
      : null;
  // following_number viaja como string en conversations, pero se normaliza por
  // si algún emisor lo manda numérico (en packages es Number).
  const followingNumber =
    meta.following_number != null && String(meta.following_number) !== ""
      ? String(meta.following_number)
      : null;

  let text;
  if (typeLabel && followingNumber) {
    text = t("Incidencia creada: {{tipo}} · pedido #{{seguimiento}}")
      .replace("{{tipo}}", typeLabel)
      .replace("{{seguimiento}}", followingNumber);
  } else if (typeLabel) {
    text = t("Incidencia creada: {{tipo}}").replace("{{tipo}}", typeLabel);
  } else {
    text = msg.content || t("Incidencia creada");
  }

  // Mismo criterio que la asignación: si getDisplayName devuelve el id crudo,
  // el autor no se resolvió y no se muestra un ObjectId.
  const createdBy = meta.created_by || null;
  const resolved = createdBy && getDisplayName ? getDisplayName(createdBy) : null;

  return {
    text,
    author: resolved && resolved !== createdBy ? resolved : null,
    incidentId: typeof meta.ref === "string" && meta.ref ? meta.ref : null,
    packageId: typeof meta.package_id === "string" && meta.package_id ? meta.package_id : null,
    followingNumber,
  };
}

// Badge de un evento de IA. El tono sale del primitivo ui/Badge (mismos tokens
// que el resto de la app); a 11px para no competir con el texto del mensaje.
function AiKindBadge({ badge }) {
  if (!badge) return null;
  const { label, tone, Icon } = badge;
  return (
    <Badge tone={tone} className="text-[11px]">
      <Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
      {label}
    </Badge>
  );
}

// Nombre del agente de un mensaje saliente: "Tú", el nombre resuelto, el
// sender_name que persistió el backend o el genérico "Agente".
function agentDisplayLabel(msg, getDisplayName, currentUserId, t) {
  if (msg.sender_id && currentUserId && msg.sender_id === currentUserId) return t("Tú");
  const resolved = getDisplayName ? getDisplayName(msg.sender_id) : "";
  return (
    (resolved && resolved !== msg.sender_id ? resolved : null) || msg.sender_name || t("Agente")
  );
}

// Remitente de un mensaje citado: la misma etiqueta que el hilo muestra para
// ese mensaje. En un DM el hilo no rotula al contacto (lo nombra el header),
// así que la cita usa el nombre del header (`contactLabel`).
function quotedSenderLabel(quoted, { isGroup, contactLabel, getDisplayName, currentUserId, t }) {
  const role = quoted?.sender_role;
  if (role === "ai") return t("Respuesta IA");
  if (role === "agent") return agentDisplayLabel(quoted, getDisplayName, currentUserId, t);
  if (role === "user") {
    const name = isGroup
      ? quoted.sender_name || quoted.participant_phone
      : contactLabel || quoted.sender_name;
    return name || t("Mensaje citado");
  }
  return t("Mensaje citado");
}

// Etiqueta de media de una cita sin texto (o prefijo de su caption).
const QUOTE_MEDIA = {
  image: { emoji: "📷", label: "Imagen" },
  audio: { emoji: "🎤", label: "Audio" },
  document: { emoji: "📄", label: "Documento" },
  video: { emoji: "🎥", label: "Video" },
  sticker: { emoji: "🏷️", label: "Sticker" },
};

// Bloque de cita dentro de la burbuja (estilo WhatsApp): borde izquierdo de
// color, remitente y hasta 2 líneas del texto citado. Si el mensaje citado está
// en el hilo cargado es un botón que salta a él; si no, un bloque inerte.
function QuoteBlock({ senderLabel, text, kind, surface, onJump }) {
  const { t } = useTranslation();
  const media = QUOTE_MEDIA[kind];
  let preview = text;
  if (media) preview = text ? `${media.emoji} ${text}` : `${media.emoji} ${t(media.label)}`;
  const className = cn(
    "block w-full text-left rounded-lg border-l-[3px] px-2 py-1 mb-1 text-xs",
    surface === "brand" && "bg-white/15 border-white/70 text-white/90",
    surface === "ai" && "bg-bg border-brand-500 text-fg-muted",
    surface === "plain" && "bg-bg-muted border-brand-500 text-fg-muted",
    onJump &&
      cn(
        "cursor-pointer transition-colors motion-reduce:transition-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        surface === "brand" ? "hover:bg-white/25" : "hover:bg-bg-subtle",
      ),
  );
  const body = (
    <>
      <span
        className={cn(
          "block font-semibold truncate",
          surface === "brand" ? "text-white" : "text-brand-700",
        )}
      >
        {senderLabel}
      </span>
      {/* line-clamp fija su propio display: no combinar con `block`. */}
      {preview && <span className="line-clamp-2 whitespace-pre-wrap">{preview}</span>}
    </>
  );
  if (!onJump) return <div className={className}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onJump}
      className={className}
      title={t("Ir al mensaje citado")}
      aria-label={`${t("Ir al mensaje citado")}: ${senderLabel}${preview ? ` — ${preview}` : ""}`}
    >
      {body}
    </button>
  );
}

// ─── Fila de mensaje (memoizada) ─────────────────────────────────────────────
// Todas las props son primitivas o referencias estables, así que React.memo
// funciona de verdad: la fila se vuelve a pintar SOLO cuando cambia su propio
// mensaje (incluido `delivery_status`, que llega como objeto nuevo dentro de un
// array nuevo), su posición dentro del run, el término/índice de búsqueda, su
// realce o el resolvedor de nombres. Cualquier otro estado del detalle
// (borrador del rename, texto del composer, menús, foco, lightbox) ya no la
// toca. Con 4.944 mensajes en el hilo esa diferencia es de ~200 ms POR TECLA.
const MessageRow = React.memo(function MessageRow({
  msg,
  isGroup,
  isFirstOfRun,
  isLastOfRun,
  daySepTs,
  // Número de caso si esta fila abre un caso nuevo, o null. PRIMITIVO a
  // propósito: cualquier objeto acá rompería el React.memo de la fila.
  caseSepNumber,
  // Índice caso → ticket / caso → entrada sellada de la conversación (mismo
  // objeto estable para todas las filas: ver caseIndexFor).
  caseIndex,
  getDisplayName,
  currentUserId,
  isWhatsAppChannel,
  searchTerm,
  searchActive,
  matchBase,
  isFlashing,
  isActiveAiMatch,
  registerRef,
  onImageOpen,
  // Mensaje del hilo citado por este (misma referencia del array `messages`,
  // o null) y nombre del contacto del DM: ambos estables para el React.memo.
  quotedMsg,
  contactLabel,
  onQuoteJump,
  // Abre el detalle de una incidencia desde su nota de sistema. Opcional (la
  // bandeja clásica no la pasa: la nota queda solo como texto) y estable.
  onOpenIncident,
}) {
  const { t } = useTranslation();
  // Ref estable por mensaje: si fuera una arrow inline, cada re-render de la
  // fila desmontaría/remontaría la entrada de messageRefs (de la que dependen
  // el salto a la coincidencia de búsqueda y el realce).
  const setRef = useCallback((el) => registerRef(msg.id, el), [registerRef, msg.id]);

  // Los mensajes omni (WhatsApp/Evolution) traen `createdAt` (timestamps
  // de Mongo, camelCase); los del portal/IA `created_at`. Leemos ambas
  // formas: sin esto, la hora y los separadores de día NO aparecían en
  // las conversaciones de WhatsApp (se leía solo created_at → undefined).
  const ts = msg.created_at || msg.createdAt || null;

  // Separador temporal: se emite antes del primer mensaje del día.
  // Second visual pass: hairlines + quiet label instead of a filled pill (the
  // thread had three pill styles competing). fg-muted keeps 11px text AA.
  const daySep = daySepTs ? (
    <div className="mt-[20px] mb-[4px] flex items-center gap-[12px] select-none" aria-hidden="true">
      <span className="h-px flex-1 bg-border/70" />
      <span className="text-[11px] leading-[16px] font-medium text-fg-muted">
        {t(daySeparatorLabel(daySepTs))}
      </span>
      <span className="h-px flex-1 bg-border/70" />
    </div>
  ) : null;

  // Separador de caso: tinte brand a propósito (corte operativo, no
  // cronológico; text-brand-700 sobre bg-brand-50 cumple AA). Cuando aparece,
  // el llamador ya suprimió el separador de día: este pill carga la fecha.
  // Same hairline geometry as the day separator; the case cut keeps a
  // filled neutral label so it reads as an operative boundary.
  const caseSep = caseSepNumber ? (
    <div className="mt-[20px] mb-[4px] flex items-center gap-[12px] select-none" aria-hidden="true">
      <span className="h-px flex-1 bg-border" />
      <span className="shrink-0 text-[11px] leading-[16px] font-semibold text-fg bg-bg-subtle rounded-full px-[10px] py-[2px]">
        {caseSeparatorLabel(
          caseSepNumber,
          caseIndex?.tickets.get(caseSepNumber) ?? null,
          ts,
          t,
        )}
      </span>
      <span className="h-px flex-1 bg-border" />
    </div>
  ) : null;

  // Evento de IA (acción confirmada/ejecutada, nota interna): badge discreto,
  // calculado una vez y usado en las dos ramas de render.
  const aiBadge = aiMessageBadge(msg, t);

  if (msg.sender_role === "system") {
    // Nota privada del equipo: burbuja propia (ámbar), no un pill de sistema.
    if (isPrivateNote(msg)) {
      return (
        <>
          {caseSep}
          {daySep}
          <div ref={setRef}>
            <PrivateNoteBubble
              msg={msg}
              getDisplayName={getDisplayName}
              currentUserId={currentUserId}
            />
          </div>
        </>
      );
    }
    // Mensajes system de asignación: se reconstruye el texto en el
    // idioma del usuario con getDisplayName; si algún id no resuelve
    // (getDisplayName devuelve el id crudo), cae al content del backend.
    const meta = msg.metadata;
    const isAssignment = meta?.kind === "assignment";
    // Reapertura de caso: el contacto volvió a escribir tras un resolve. Se
    // reconstruye el texto en el idioma del usuario a partir de la metadata;
    // sin metadata cae al content que persistió el backend.
    const isCaseReopen = meta?.kind === "case_reopened" && !!meta.case_number;
    let systemText = msg.content;
    if (isCaseReopen) {
      systemText = caseReopenLabel(
        meta.case_number,
        meta.ticket_number ?? caseIndex?.tickets.get(meta.case_number) ?? null,
        t,
      );
    }
    if (isAssignment) {
      const agentName =
        meta.assigned_to && getDisplayName ? getDisplayName(meta.assigned_to) : null;
      const byName =
        meta.assigned_by && getDisplayName ? getDisplayName(meta.assigned_by) : null;
      const agentOk = agentName && agentName !== meta.assigned_to;
      const byOk = byName && byName !== meta.assigned_by;
      if (meta.self && agentOk) {
        systemText = t("{{agente}} tomó el caso").replace("{{agente}}", agentName);
      } else if (!meta.self && agentOk && byOk) {
        systemText = t("{{asignador}} asignó el caso a {{agente}}")
          .replace("{{asignador}}", byName)
          .replace("{{agente}}", agentName);
      }
    }

    // Cierre de caso: cierra el ciclo que abre el separador de caso, así que se
    // pinta como DIVISOR (líneas + pill) y no como un pill de sistema común.
    // Todo lo que el backend no mandó en la metadata se completa con la entrada
    // sellada de case_history del mismo caso.
    if (meta?.kind === "case_closed") {
      const closedNumber = meta.case_number ?? msg.case_number ?? 1;
      const entry = caseIndex?.entries.get(closedNumber) ?? null;
      const label = caseClosedLabel({
        caseNumber: closedNumber,
        ticketNumber:
          meta.ticket_number ?? caseIndex?.tickets.get(closedNumber) ?? null,
        resolvedByName: closedByName(
          meta.resolved_by ?? entry?.resolved_by,
          getDisplayName,
        ),
        origin: meta.origin ?? entry?.origin ?? null,
        t,
      });
      const note = meta.note ?? entry?.note ?? null;
      return (
        <>
          {caseSep}
          {daySep}
          <div role="status" className="my-4 flex flex-col items-center">
            <div className="flex w-full items-center gap-3">
              <span className="h-px flex-1 bg-border" aria-hidden="true" />
              <span className="inline-flex items-center gap-1.5 shrink-0 px-3 py-1 rounded-full bg-status-success-soft text-status-success text-[11px] font-medium">
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {label}
              </span>
              <span className="h-px flex-1 bg-border" aria-hidden="true" />
            </div>
            {/* Nota del cierre (p. ej. el motivo de un cierre masivo): 2 líneas
                como máximo, con el texto completo en el title. */}
            {note && (
              <p
                className="mt-1.5 max-w-[80%] text-center text-[11px] text-fg-muted line-clamp-2"
                title={note}
              >
                {note}
              </p>
            )}
          </div>
        </>
      );
    }

    // Incidencia creada desde la conversación: pill propio con el tipo, el
    // pedido y el autor; "Ver incidencia" solo si el contenedor sabe abrirla
    // (sin la prop no se pinta un botón que no lleva a ningún lado).
    const incidentNote = systemNoteLabel(msg, t, getDisplayName);
    if (incidentNote) {
      const canOpenIncident = !!onOpenIncident && !!incidentNote.incidentId;
      return (
        <>
          {caseSep}
          {daySep}
          <div
            role="status"
            className="mt-[16px] flex flex-wrap items-center justify-center gap-x-2 gap-y-1"
          >
            <span className="inline-flex items-center gap-[6px] max-w-full px-[10px] py-[4px] rounded-lg bg-bg-subtle/70 text-fg-muted text-[12px] leading-[16px]">
              <AlertTriangle
                className="w-3 h-3 shrink-0 text-status-warning"
                aria-hidden="true"
              />
              <span className="min-w-0">
                {incidentNote.text}
                {incidentNote.author && ` · ${incidentNote.author}`}
              </span>
            </span>
            {canOpenIncident && (
              <button
                type="button"
                onClick={() => onOpenIncident(incidentNote.incidentId, incidentNote.packageId)}
                aria-label={
                  incidentNote.followingNumber
                    ? t("Ver incidencia del pedido #{{seguimiento}}").replace(
                        "{{seguimiento}}",
                        incidentNote.followingNumber,
                      )
                    : undefined
                }
                className="hd-touch-btn inline-flex items-center h-6 px-2 rounded-md text-[11px] font-medium text-brand-700 underline underline-offset-2 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors motion-reduce:transition-none"
              >
                {t("Ver incidencia")}
              </button>
            )}
          </div>
        </>
      );
    }

    return (
      <>
        {caseSep}
        {daySep}
        {/* Con badge de IA o de automatización el pill va debajo (columna
            centrada); sin ellos, el render queda idéntico al de siempre. */}
        {/* Evento de sistema: texto centrado y discreto, sin pill rellena.
            Puede ser largo (motivo de derivación de la IA): ancho acotado y
            leading cómodo en vez de una burbuja gris enorme. */}
        <div
          role="status"
          className={cn(
            "mt-[16px] flex",
            aiBadge || meta?.rule_name ? "flex-col items-center gap-[4px]" : "justify-center",
          )}
        >
          <AiKindBadge badge={aiBadge} />
          <AutomationTag ruleName={meta?.rule_name} />
          {systemText && (
            <span className="block max-w-[min(88%,34rem)] px-[12px] text-center text-[12px] leading-[18px] text-fg-muted break-words">
              {isAssignment && (
                <UserCheck className="inline w-3 h-3 mr-[4px] -mt-[2px] align-middle" aria-hidden="true" />
              )}
              {isCaseReopen && (
                <RotateCcw className="inline w-3 h-3 mr-[4px] -mt-[2px] align-middle" aria-hidden="true" />
              )}
              {systemText}
            </span>
          )}
        </div>
      </>
    );
  }

  const isUser = msg.sender_role === "user";
  const isAI = msg.sender_role === "ai";
  const isAgent = msg.sender_role === "agent";

  // Mensaje solo-imagen: burbuja con padding mínimo y la hora como
  // pill superpuesta sobre la imagen (estilo WhatsApp).
  const isImageOnly = msg.content_type === "image" && !!msg.media_url && !msg.content;
  // Sticker: se renderiza SIN burbuja (fondo/borde/sombra) y con una
  // etiqueta explícita, para que no se confunda con una foto pequeña.
  const isSticker = msg.content_type === "sticker" && !!msg.media_url;
  const timeStr = ts ? dayjs(ts).format("HH:mm") : "";

  // ─ Atribución por mensaje en grupos ──────────────────────────────
  // En grupos cualquier participante puede escribir, así que la
  // burbuja "user" necesita decir QUIÉN dentro del grupo lo envió.
  // pushName (sender_name) es lo único confiable que da Evolution;
  // participant_phone solo viene en inbound (Evolution no lo expone
  // para el propio agente en outbound).
  const groupSenderLabel =
    isGroup && isUser ? msg.sender_name || msg.participant_phone || null : null;

  // ─ Origen del outbound (fromMe) ──────────────────────────────────
  // El badge solo aparece cuando el mensaje vino "echoed" por
  // Evolution desde el teléfono o WhatsApp Web del propio agente —
  // los enviados desde nuestra app no llevan from_me=true en DB
  // (se persisten con sender_role='agent' pero from_me=false). Esto
  // permite al supervisor saber qué cliente fue el origen.
  const fromOtherClient = isAgent && msg.from_me;
  const fromMeSource = msg.metadata?.source;
  const fromMeIcon =
    fromMeSource === "web" ? (
      <Globe className="w-3 h-3" aria-hidden="true" />
    ) : (
      <Smartphone className="w-3 h-3" aria-hidden="true" />
    );
  const fromMeLabel =
    fromMeSource === "web" ? t("Desde WhatsApp Web") : t("Desde el teléfono");

  // Inicial del avatar para la burbuja "user":
  //  - En grupos preferimos pushName (sender_name) porque sender_id
  //    es el group JID (no aporta), o el LID (mismo problema).
  //  - En DMs mantenemos la lógica original.
  const userAvatarInitial = isGroup
    ? (msg.sender_name?.[0] || msg.participant_phone?.[0] || "?").toUpperCase()
    : ((getDisplayName ? getDisplayName(msg.sender_id) : "U")?.[0]?.toUpperCase() || "U");

  // ─ Etiqueta de remitente (solo en el primer mensaje del run) ────
  // En DM el remitente entrante es SIEMPRE el contacto que ya nombra
  // el header: repetirlo sobre cada run (y los runs se cortan a los
  // 5 min) era la mayor fuente de ruido del hilo. Solo los grupos
  // necesitan atribución por mensaje. Agente: nombre real (o "Tú").
  const agentLabel = isAgent ? agentDisplayLabel(msg, getDisplayName, currentUserId, t) : null;
  const userLabel = groupSenderLabel;

  // ─ Cita (respuesta a otro mensaje) ──────────────────────────────
  // Con el mensaje citado en el hilo se usa su contenido y su remitente; si
  // no está cargado, el texto que trae el propio payload y un rótulo genérico.
  const quote = extractQuote(msg);
  let quoteBlock = null;
  if (quote) {
    const preview = quotedMsg
      ? previewOfThreadMessage(quotedMsg)
      : { text: quote.text, kind: quote.kind };
    quoteBlock = (
      <QuoteBlock
        senderLabel={
          quotedMsg
            ? quotedSenderLabel(quotedMsg, {
                isGroup,
                contactLabel,
                getDisplayName,
                currentUserId,
                t,
              })
            : t("Mensaje citado")
        }
        text={preview.text}
        kind={preview.kind}
        surface={isAgent && !isSticker ? "brand" : isAI && !isSticker ? "ai" : "plain"}
        onJump={quotedMsg && onQuoteJump ? () => onQuoteJump(quotedMsg.id) : null}
      />
    );
  }

  return (
    <>
      {caseSep}
      {daySep}
      <div
        ref={setRef}
        className={cn(
          "flex gap-2 items-end",
          isUser ? "justify-start" : "justify-end",
          isFirstOfRun ? "mt-[16px]" : "mt-[2px]",
        )}
      >
        {/* Avatar usuario (izq): solo en grupos y solo en el último del
            run. En un DM el remitente entrante es siempre el mismo, y
            cuando el contacto es un teléfono la inicial era un dígito
            ("5") repetido junto a cada burbuja: 28px de ancho que no
            identificaban nada. */}
        {isUser &&
          isGroup &&
          (isLastOfRun ? (
            <div className="w-7 h-7 rounded-full bg-bg-subtle text-fg-muted flex items-center justify-center text-[11px] font-semibold shrink-0">
              {userAvatarInitial}
            </div>
          ) : (
            <div className="w-7 shrink-0" aria-hidden="true" />
          ))}

        <div
          className={cn(
            "max-w-[85%] sm:max-w-[70%] flex flex-col",
            isUser ? "items-start" : "items-end",
          )}
        >
          {/* Etiqueta remitente (primer mensaje del run) */}
          {isUser && isFirstOfRun && userLabel && (
            <div
              className="text-[11px] leading-[16px] text-fg-muted mb-[4px] px-[4px] font-medium truncate max-w-full"
              title={userLabel}
            >
              {userLabel}
              {groupSenderLabel &&
                msg.participant_phone &&
                msg.participant_phone !== msg.sender_name &&
                ` · ${msg.participant_phone}`}
            </div>
          )}
          {!isUser && isFirstOfRun && (
            <div className="text-[11px] leading-[16px] text-fg-muted mb-[4px] px-[4px] flex items-center gap-1 flex-wrap">
              {isAI ? (
                <>
                  <Bot className="w-3 h-3" aria-hidden="true" />
                  <span className="font-medium">{t("Respuesta IA")}</span>
                </>
              ) : (
                <span className="font-medium">{agentLabel}</span>
              )}
              {/* Origen del outbound: dato de auditoría. Como pill con
                  texto duplicaba el ancho del encabezado en cada run;
                  ícono + tooltip basta (el avatar de cierre de run
                  repite el mismo ícono). */}
              {fromOtherClient && (
                <span
                  className="inline-flex items-center text-fg-muted"
                  title={fromMeLabel}
                  aria-label={fromMeLabel}
                  role="img"
                >
                  {fromMeIcon}
                </span>
              )}
            </div>
          )}

          {/* Badge de acción de IA: afuera de la burbuja a propósito — marca el
              mensaje sin teñir su contenido ni competir con él. */}
          {aiBadge && (
            <div className="mb-0.5">
              <AiKindBadge badge={aiBadge} />
            </div>
          )}

          {/* Burbuja */}
          <div
            className={cn(
              "relative text-[14px] leading-[20px] break-words",
              "transition-shadow duration-500 motion-reduce:transition-none",
              // Los stickers de WhatsApp van "sueltos", con
              // transparencia: encerrarlos en el fondo de la burbuja los
              // hace parecer una imagen recortada.
              isSticker
                ? "bg-transparent p-0 shadow-none border-0"
                : cn(
                    // Flat bubbles (no shadow): border OR fill, never both.
                    "rounded-[16px]",
                    isImageOnly ? "p-[4px]" : "px-[12px] py-[8px]",
                    isUser && "bg-bg text-fg border border-border/80",
                    isUser && isLastOfRun && "rounded-bl-[6px]",
                    isAgent && "hd-brand-solid bg-brand-500 text-white",
                    // Neutral, not brand-50: the peach tint made an AI reply (sent
                    // to the contact) read like a private note (amber, internal).
                    // The "Respuesta IA" label and the bot avatar identify it.
                    isAI && "bg-bg-subtle text-fg",
                    !isUser && isLastOfRun && "rounded-br-[6px]",
                  ),
              (isFlashing || isActiveAiMatch) && "ring-2 ring-brand-500",
            )}
          >
            {quoteBlock}
            {/* Media */}
            <MediaRenderer msg={msg} onImageOpen={onImageOpen} />
            {msg.content && (
              <div
                className={cn(
                  isAI && "prose prose-sm max-w-none prose-p:my-1",
                  "whitespace-pre-wrap",
                )}
              >
                {isAI ? (
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                ) : (
                  renderWhatsApp(msg.content, {
                    format: isWhatsAppChannel,
                    query: searchTerm,
                    activeIndex: searchActive,
                    matchBase,
                  })
                )}
              </div>
            )}

            {/* Sticker: etiqueta explícita + hora, fuera de toda burbuja */}
            {isSticker && (
              <div
                className={cn(
                  "mt-1 flex items-center gap-1.5 text-[11px] text-fg-muted select-none",
                  isUser ? "justify-start" : "justify-end",
                )}
              >
                <span className="inline-flex items-center gap-1">
                  <Sticker className="w-3 h-3" aria-hidden="true" />
                  {t("Sticker")}
                </span>
                {timeStr && <span>{timeStr}</span>}
                {!isUser && <DeliveryReceipt status={msg.delivery_status} surface="plain" />}
              </div>
            )}

            {/* Hora + recibo de entrega dentro de la burbuja (abajo-derecha) */}
            {!isSticker &&
              timeStr &&
              (isImageOnly ? (
                <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/55 text-white text-[11px] pointer-events-none select-none">
                  {timeStr}
                  {/* La pill es negra translúcida, no la burbuja naranja. */}
                  {!isUser && <DeliveryReceipt status={msg.delivery_status} surface="dark" />}
                </span>
              ) : (
                <div
                  className={cn(
                    "text-[11px] mt-0.5 flex items-center justify-end gap-1 select-none",
                    // fg-muted, no fg-subtle: sobre la burbuja del
                    // cliente (bg) fg-subtle da 2.68:1 en claro.
                    isAgent ? "text-white/80" : "text-fg-muted",
                  )}
                >
                  {timeStr}
                  {!isUser && (
                    <DeliveryReceipt
                      status={msg.delivery_status}
                      surface={isAgent ? "brand" : "plain"}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>

        {/* Avatar agente/IA (der): solo en el último del run */}
        {!isUser &&
          (isLastOfRun ? (
            <div
              // Neutral: an orange avatar next to the orange bubble repeated
              // the accent without adding information.
              className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 bg-bg-subtle text-fg-muted ring-1 ring-border/70"
            >
              {isAI ? (
                <Bot className="w-3.5 h-3.5" aria-hidden="true" />
              ) : fromOtherClient ? (
                fromMeSource === "web" ? (
                  <Globe className="w-3.5 h-3.5" aria-hidden="true" />
                ) : (
                  <Smartphone className="w-3.5 h-3.5" aria-hidden="true" />
                )
              ) : (
                ((getDisplayName ? getDisplayName(msg.sender_id) : "A")?.[0] || "A").toUpperCase()
              )}
            </div>
          ) : (
            <div className="w-7 shrink-0" aria-hidden="true" />
          ))}
      </div>
    </>
  );
});

// ─── Hilo de mensajes (memoizado) ────────────────────────────────────────────
// Barrera de re-render: mientras `messages`, la búsqueda, el realce, el
// resolvedor de nombres y el flag de grupo no cambien, teclear en cualquier
// input del detalle NO recorre los miles de mensajes del hilo.
const MessageThread = React.memo(function MessageThread({
  messages,
  isGroup,
  getDisplayName,
  currentUserId,
  caseIndex,
  isWhatsAppChannel,
  searchTerm,
  searchActive,
  searchActiveId,
  matchBaseFor,
  flashId,
  registerRef,
  onImageOpen,
  contactLabel,
  onQuoteJump,
  onOpenIncident,
}) {
  // Índice external_message_id → mensaje, para resolver las citas del hilo.
  const byExternalId = React.useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      if (m?.external_message_id) map.set(m.external_message_id, m);
    }
    return map;
  }, [messages]);
  return messages.map((msg, idx) => {
    const prev = idx > 0 ? messages[idx - 1] : null;
    const next = idx < messages.length - 1 ? messages[idx + 1] : null;
    const ts = msg.created_at || msg.createdAt || null;
    const prevTs = prev ? prev.created_at || prev.createdAt || null : null;
    const showDaySep = !!ts && (!prevTs || !dayjs(ts).isSame(dayjs(prevTs), "day"));
    // Corte de caso: los mensajes legacy no traen case_number (= caso 1).
    // Nunca hay divisor antes del primer mensaje del hilo (prevCase adopta
    // msgCase cuando no hay `prev`).
    const msgCase = msg.case_number || 1;
    const prevCase = prev ? prev.case_number || 1 : msgCase;
    const showCaseSep = prevCase !== msgCase;
    const quote = extractQuote(msg);
    const quotedMsg = (quote && byExternalId.get(quote.stanzaId)) || null;
    return (
      <MessageRow
        key={msg.id}
        msg={msg}
        isGroup={isGroup}
        // Runs de mensajes consecutivos: nombre solo en el primero del run,
        // avatar solo en el último, esquina "cola" solo al final del run.
        isFirstOfRun={!isSameRun(prev, msg, isGroup)}
        isLastOfRun={!isSameRun(msg, next, isGroup)}
        // Con corte de caso, el pill de caso lleva la fecha y se suprime el de
        // día: dos pills apilados eran puro ruido. Ambos props PRIMITIVOS.
        caseSepNumber={showCaseSep ? msgCase : null}
        caseIndex={caseIndex}
        daySepTs={showDaySep && !showCaseSep ? ts : null}
        getDisplayName={getDisplayName}
        currentUserId={currentUserId}
        isWhatsAppChannel={isWhatsAppChannel}
        searchTerm={searchTerm}
        searchActive={searchActive}
        matchBase={matchBaseFor(msg.id)}
        isFlashing={flashId === msg.id}
        // Resaltado de búsqueda: los mensajes normales llevan <mark> dentro
        // del parser; los de IA (ReactMarkdown) se marcan a nivel de burbuja
        // mientras son la coincidencia activa.
        isActiveAiMatch={
          msg.sender_role === "ai" && !!searchTerm && searchActiveId === msg.id
        }
        registerRef={registerRef}
        onImageOpen={onImageOpen}
        quotedMsg={quotedMsg !== msg ? quotedMsg : null}
        contactLabel={contactLabel}
        onQuoteJump={onQuoteJump}
        onOpenIncident={onOpenIncident}
      />
    );
  });
});

// ─── Modal: renombrar grupo ───────────────────────────────────────────────────
// Vive en su propio componente con el borrador en estado LOCAL. Antes el
// borrador era estado de ConversationDetail: cada tecla re-renderizaba el
// detalle entero —hilo de mensajes incluido— y el input se sentía "guardando
// letra por letra". Acá el estado ni siquiera sale del modal.
//
// Los grupos de WhatsApp llegan identificados por el JID numérico del grupo
// ("120363382372162507…"): sin un nombre puesto a mano no hay forma de saber
// de qué grupo se trata. El endpoint todavía no está desplegado, así que un
// 404/405 se degrada a un aviso "pendiente de backend", no a un error rojo.
function RenameGroupModal({ open, conversation, currentName, onClose, onUpdated }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(currentName || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  // El modal no se desmonta al cerrarse (Modal devuelve null pero el
  // componente sigue montado), así que el borrador se siembra en cada APERTURA
  // con el nombre vigente. Se resiembra solo en la transición cerrado→abierto,
  // nunca mientras está abierto: si el SSE actualiza la conversación a mitad
  // de la edición, el nombre que está escribiendo el agente no se pisa.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setDraft(currentName || "");
      setError(null);
      setPending(false);
    }
    wasOpenRef.current = open;
  }, [open, currentName]);

  const trimmed = draft.trim();

  const handleSubmit = useCallback(async () => {
    const name = draft.trim();
    if (!name || name.length > MAX_GROUP_NAME || saving) return;
    setSaving(true);
    setError(null);
    setPending(false);
    try {
      const updated = await renameConversation(conversation.id, name, conversation.channel);
      // El backend aún no existe: puede devolver la conversación plana o
      // envuelta en { conversation }. onUpdated (handleConvUpdated del inbox)
      // espera el objeto plano con `.id`.
      const conv = updated?.conversation ?? updated;
      if (conv?.id) onUpdated?.(conv);
      onClose?.();
    } catch (err) {
      // Mismo criterio que ContactPanel: el aviso "pendiente de backend" es
      // SOLO para un 404/405 sin mensaje del backend (ruta no desplegada en ese
      // entorno). Con mensaje de Nest ('Conversacion no encontrada') es un error
      // real, y se muestra el texto del backend o el fallback traducido.
      if (isEndpointMissing(err) && !err?.serverMessage) {
        setPending(true);
      } else {
        setError(apiErrorMessage(err, t("No se pudo renombrar el grupo.")));
      }
    } finally {
      setSaving(false);
    }
  }, [draft, saving, conversation?.id, conversation?.channel, onUpdated, onClose, t]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Renombrar grupo")}
      description={t("Este nombre solo se usa en el helpdesk; no cambia el nombre del grupo en WhatsApp.")}
      size="sm"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 rounded-md text-sm font-medium text-fg hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
          >
            {t("Cancelar")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !trimmed || trimmed.length > MAX_GROUP_NAME}
            className="hd-brand-solid inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium bg-brand-500 text-bg hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
          >
            {saving && (
              <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
            )}
            {saving ? t("Guardando…") : t("Guardar")}
          </button>
        </>
      }
    >
      <label
        htmlFor="hd-rename-group"
        className="hd-field-label block text-[11px] uppercase tracking-wider font-semibold text-fg-subtle mb-1.5"
      >
        {t("Nombre del grupo")}
      </label>
      <input
        id="hd-rename-group"
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
        placeholder={conversation?.external_id || t("Nombre del grupo")}
        maxLength={MAX_GROUP_NAME}
        disabled={saving}
        aria-describedby="hd-rename-group-hint"
        className="w-full h-9 px-2.5 rounded-md border border-border bg-bg text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
      />
      <div
        id="hd-rename-group-hint"
        className="mt-1.5 flex items-center justify-between gap-2 text-[11px] text-fg-subtle"
      >
        <span className="truncate">
          {t("ID del grupo")}: <code className="font-mono">{conversation?.external_id}</code>
        </span>
        <span className="shrink-0 tabular-nums">
          {trimmed.length}/{MAX_GROUP_NAME}
        </span>
      </div>

      {pending && (
        <PendingBackendNotice className="mt-3">
          {t("Renombrar grupos está en construcción.")}
        </PendingBackendNotice>
      )}
      {error && (
        <div
          role="alert"
          className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-status-error-soft text-status-error text-xs"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </Modal>
  );
}

// ─── Pestañas del composer: Responder / Nota privada ─────────────────────────
// Patrón ARIA tabs con flechas. La nota se tiñe de ámbar (mismo tono que el
// editor de notas) para que el modo activo se lea de un vistazo.
function ComposerModeTabs({ mode, onChange, noteDisabledReason }) {
  const { t } = useTranslation();
  const tabs = [
    { value: "reply", label: t("Responder"), Icon: Reply },
    { value: "note", label: t("Nota privada"), Icon: Lock, disabledReason: noteDisabledReason },
  ];
  const focusTab = (e, value) => {
    const list = e.currentTarget.closest('[role="tablist"]');
    list?.querySelector(`[data-mode="${value}"]`)?.focus();
  };
  return (
    <div
      role="tablist"
      aria-label={t("Tipo de mensaje")}
      className="inline-flex items-center gap-[2px]"
      onKeyDown={(e) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const next = mode === "reply" ? "note" : "reply";
        if (next === "note" && noteDisabledReason) return;
        onChange(next);
        focusTab(e, next);
      }}
    >
      {tabs.map(({ value, label, Icon, disabledReason }) => {
        const active = mode === value;
        return (
          <button
            key={value}
            type="button"
            role="tab"
            data-mode={value}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            disabled={!!disabledReason}
            title={disabledReason || undefined}
            onClick={() => onChange(value)}
            className={cn(
              "inline-flex items-center gap-[6px] h-[36px] md:h-[28px] px-[10px] rounded-md text-[12px] font-medium whitespace-nowrap transition-colors motion-reduce:transition-none",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              active
                ? value === "note"
                  ? "bg-status-warning-soft text-status-warning dark:bg-status-warning/15"
                  : "bg-bg-subtle text-fg"
                : "text-fg-muted hover:bg-bg-muted hover:text-fg",
            )}
          >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ─── ConversationDetail ───────────────────────────────────────────────────────
export default function ConversationDetail({
  conversation,
  messages: rawMessages,
  loading,
  onUpdated,
  onTakeover,
  onBack,
  getDisplayName,
  onToggleContactPanel,
  contactPanelOpen,
  // Carpetas del helpdesk: lista (para el picker), índice por id (memo estable
  // del padre) y mover-a-carpeta. Nada de esto baja al hilo memoizado.
  folders,
  foldersById,
  onMoveToFolder,
  // Fallo al CARGAR la conversación (no de una acción del detalle): lo reporta
  // el padre desde loadDetail, junto con el reintento. Ambos opcionales: sin
  // ellos el detalle se comporta como antes.
  detailError,
  onRetryDetail,
  // Número (channel_account) de la conversación, tomado de la fila de la
  // lista: el detalle no lo trae. Avisa si la respuesta saldrá por otro número.
  channelAccount,
  // Fila de acciones del contenedor (p. ej. "Crear incidencia" en la vista de
  // Atención e incidencias), justo debajo del header. Sin ella no se pinta
  // nada: la bandeja clásica queda igual.
  contextBar,
  // (incidentId, packageId) => void: habilita "Ver incidencia" en las notas
  // `incident_created` del hilo. Sin ella la nota queda solo como texto.
  onOpenIncident,
  // () => void: relee el detalle abierto sin spinner (tras correr una macro).
  // Opcional: sin ella, los mensajes nuevos llegan solo por el SSE.
  onRefreshConversation,
  // Estado del fijado del usuario y la acción que lo alterna. `onTogglePin`
  // solo llega si la conversación es fijable y el backend expone los fijados:
  // sin ella el menú "Más acciones" no muestra la entrada de fijar.
  pinned = false,
  onTogglePin,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  // Below 1440px the header has no room for the macros icon next to the
  // primary action without crushing the contact name (1280px: ~136px left for
  // it): macros move into the overflow menu, same entry as on phones.
  const macrosInMenu = useIsMobile(1440);
  // Grupos: "@<número>" → "@Nombre" en mensajes guardados antes de que el
  // backend resolviera las menciones. Antes que nada, así hilo, búsqueda y
  // citas ven el mismo texto.
  const isGroupConversation = !!conversation?.is_group;
  const routeNotice = replyRouteNotice(channelAccount, conversation?.channel, t);
  const messages = React.useMemo(
    () => (isGroupConversation ? withGroupMentionNames(rawMessages) : rawMessages),
    [isGroupConversation, rawMessages],
  );
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  // Popover de selección de agente (asignar/reasignar a un tercero)
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  // Popover del historial de casos (ancla: chip "Caso #N" del header).
  const [caseHistoryOpen, setCaseHistoryOpen] = useState(false);
  // Popover "Mover a carpeta" (se abre desde el menú overflow).
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [showConfirmResolve, setShowConfirmResolve] = useState(false);
  // Imagen abierta en el lightbox: { url, caption } | null
  const [lightbox, setLightbox] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  // Grabación de nota de voz activa: reemplaza el composer por el grabador.
  const [voiceActive, setVoiceActive] = useState(false);
  const [filePreview, setFilePreview] = useState(null);
  // sidePanelOpen es para el modo overlay (mobile/tablet). En desktop wide,
  // el padre controla la 3ra columna fija vía onToggleContactPanel.
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Popover de respuestas rápidas sobre el composer.
  const [quickOpen, setQuickOpen] = useState(false);
  // El hint de atajos solo aparece con el composer enfocado: en reposo compite
  // visualmente con el hilo sin aportar nada.
  const [composerFocused, setComposerFocused] = useState(false);
  // Mensaje con realce temporal tras saltar a una coincidencia de búsqueda.
  const [flashId, setFlashId] = useState(null);
  // Renombrado de grupo (solo conversaciones con is_group). Acá SOLO vive el
  // "está abierto": el borrador, el guardado y sus errores son estado interno
  // de RenameGroupModal, para que teclear el nombre no re-renderice el detalle.
  const [renameOpen, setRenameOpen] = useState(false);
  // Composer: responder al contacto o nota privada al equipo. El editor de la
  // nota se monta la primera vez que se elige la pestaña y luego solo se
  // oculta, para que su borrador sobreviva a ir y volver entre pestañas.
  const [composerMode, setComposerMode] = useState("reply");
  const [noteMounted, setNoteMounted] = useState(false);
  // El texto del composer viene del copiloto (se marca como borrador).
  const [aiDraft, setAiDraft] = useState(false);
  // Etiquetas: selector abierto y la etiqueta cuyo cambio está en vuelo.
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const [labelPendingId, setLabelPendingId] = useState(null);
  const [macrosOpen, setMacrosOpen] = useState(false);
  const labelBtnRef = useRef(null);
  const macrosBtnRef = useRef(null);
  const menuRef = useRef(null);
  const menuBtnRef = useRef(null);
  const messagesEndRef = useRef(null);
  // Contenedor scrolleable del hilo y wrapper de su contenido. El wrapper existe
  // para el ResizeObserver: el contenedor no cambia de tamaño cuando crece su
  // contenido (media que carga tarde), solo su scrollHeight.
  const messagesContainerRef = useRef(null);
  const messagesInnerRef = useRef(null);
  // ¿El agente está pegado al fondo? Se recalcula en cada scroll del contenedor.
  const atBottomRef = useRef(true);
  // Último scrollTop observado, para distinguir un scroll DEL AGENTE de uno
  // provocado por el hilo al crecer (ver handleThreadScroll).
  const lastScrollTopRef = useRef(0);
  // Id de la conversación cuyo salto instantáneo de apertura ya se ejecutó.
  const initialScrollDoneForRef = useRef(null);
  // Espejo de (searchOpen && searchTerm) para leerlo desde el ResizeObserver sin
  // re-suscribirlo en cada tecla del buscador.
  const searchGuardRef = useRef(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const quickBtnRef = useRef(null);
  const searchBtnRef = useRef(null);
  const rootRef = useRef(null);
  const messageRefs = useRef(new Map());
  // Registro estable de refs por mensaje (lo consume MessageRow). Debe ser
  // estable o React.memo de la fila no serviría de nada.
  const registerMessageRef = useCallback((id, el) => {
    if (el) messageRefs.current.set(id, el);
    else messageRefs.current.delete(id);
  }, []);
  // onOpenIncident baja hasta cada fila memoizada: si el padre la pasara como
  // arrow inline, cada render suyo repintaría el hilo entero. Se expone una
  // referencia estable que solo cambia al aparecer o desaparecer la prop, y que
  // siempre llama a la última versión recibida.
  const onOpenIncidentRef = useRef(onOpenIncident);
  useLayoutEffect(() => {
    onOpenIncidentRef.current = onOpenIncident;
  });
  const hasOpenIncident = typeof onOpenIncident === "function";
  const openIncident = useMemo(
    () =>
      hasOpenIncident
        ? (incidentId, packageId) => onOpenIncidentRef.current?.(incidentId, packageId)
        : undefined,
    [hasOpenIncident],
  );
  // Último mensaje ya "autoscrolleado": el hilo solo salta al fondo cuando llega
  // uno nuevo, nunca por abrir o cerrar el buscador.
  const lastMessageIdRef = useRef(null);
  // El popover de respuestas rápidas se abrió tecleando "/" (no con el botón).
  const slashOpenedRef = useRef(false);
  // Posición del caret a restaurar después de insertar una respuesta rápida.
  const pendingCaret = useRef(null);

  // Cerrar menú overflow al click fuera o al presionar Escape
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (menuBtnRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  const isSupervisor =
    user?.role === roleRoutes.supervisor ||
    user?.role === roleRoutes.admin ||
    user?.role === roleRoutes.superadmin ||
    user?.role === roleRoutes.systemSuperadmin;

  const isAIChannel = conversation?.channel === "ai";
  // Canal interno (asistente de gerencia): sin contacto detrás, así que el
  // header no tiene teléfono, comercio ni ventana de mensajería que mostrar.
  const isInternalChannel = conversation?.channel === "internal";
  const isResolved = conversation?.status === "resolved";
  const isPending = conversation?.status === "pending";
  const isWhatsAppChannel = WHATSAPP_CHANNELS.includes(conversation?.channel);
  const supportsWorkflow = supportsWorkflowTools(conversation);
  const { byId: labelsById } = useHelpdeskLabels();
  const copilot = useCopilot(supportsWorkflow ? conversation?.id : null);
  const { ask: askCopilotFor, dismiss: dismissCopilot } = copilot;
  const notifications = useDesktopNotificationsPreference();

  // ─ Búsqueda dentro del hilo ─────────────────────────────────────────────
  // Los mensajes de IA se cuentan (para poder navegarlos) pero no llevan <mark>:
  // se renderizan con ReactMarkdown, así que se resaltan a nivel de burbuja.
  const getSearchable = useCallback(
    (msg) => {
      if (!msg || msg.sender_role === "system" || !msg.content) return null;
      if (msg.sender_role === "ai") return { text: msg.content, format: false };
      return { text: msg.content, format: isWhatsAppChannel };
    },
    [isWhatsAppChannel],
  );

  const search = useThreadSearch({ messages, getSearchable, rootRef, toggleRef: searchBtnRef });
  const {
    open: searchOpen,
    term: searchTerm,
    active: searchActive,
    activeMessageId: searchActiveId,
    matchBaseFor,
    closeSearch,
    resetSearch,
  } = search;

  // ─ Composer: ¿debe mostrarse? ─
  // El bug previo: en canal "ai" se ocultaba el composer SIEMPRE. Eso impedía
  // que el agente respondiera aunque la IA hubiera contestado nonsense.
  // Nueva lógica: el composer SIEMPRE se muestra salvo que la conversación
  // esté cerrada. Para canales no-IA con status `pending`, se exige que el
  // agente tome el caso primero (igual que antes). Para canal "ai" se permite
  // responder directamente; cualquier rechazo del backend se surface inline.
  const canCompose = !isResolved && (isAIChannel || !isPending);

  // Target real para postear: si la conv tiene un `conversation_id` explícito
  // (caso AI log con backend extendido), lo usamos; si no, usamos el id
  // estándar.
  const replyTargetId = conversation?.conversation_id || conversation?.id;
  // Para AI usamos el canal portal en el POST (las conversaciones derivadas
  // de IA viven en el servicio de portal). Para los demás, mantenemos el canal.
  const replyChannel = isAIChannel ? "portal" : conversation?.channel;

  // ─ Autoscroll del hilo ────────────────────────────────────────────────────
  // Tres situaciones distintas, tres comportamientos (modelo WhatsApp Web):
  //   1) apertura / cambio de conversación → salto INSTANTÁNEO al fondo, antes
  //      del paint (useLayoutEffect + scrollTop), para que el hilo nunca se vea
  //      "a medias" ni se anime el recorrido desde arriba;
  //   2) mensaje nuevo con el hilo ya abierto → smooth, pero SOLO si el agente
  //      seguía pegado al fondo;
  //   3) contenido que crece después (imágenes/videos/audios que cargan tarde)
  //      → re-ancla instantáneo mientras siga pegado al fondo.
  // El bug que esto arregla: con scrollIntoView({behavior:"smooth"}) al abrir,
  // una imagen que cargaba a mitad de la animación reajustaba el layout y el
  // scroll quedaba varado en medio del hilo.

  // Espejo del guard del buscador (lo leen el RO y el handler de scroll).
  useEffect(() => {
    searchGuardRef.current = !!(searchOpen && searchTerm);
  }, [searchOpen, searchTerm]);

  const stickToBottom = useCallback((smooth) => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const top = el.scrollHeight;
    if (smooth && !prefersReducedMotion() && typeof el.scrollTo === "function") {
      el.scrollTo({ top, behavior: "smooth" });
    } else {
      // scrollTop directo sobre el CONTENEDOR (no scrollIntoView, que depende
      // del layout del hijo centinela y del scroll de los ancestros).
      el.scrollTop = top;
    }
    atBottomRef.current = true;
    lastScrollTopRef.current = el.scrollTop;
  }, []);

  const handleThreadScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const prevTop = lastScrollTopRef.current;
    const top = el.scrollTop;
    lastScrollTopRef.current = top;
    if (el.scrollHeight - top - el.clientHeight <= STICK_TO_BOTTOM_PX) {
      atBottomRef.current = true;
      return;
    }
    // Aquí la distancia al fondo es grande, pero eso NO implica que el agente
    // se haya despegado: mientras cargan las imágenes del hilo el scrollHeight
    // crece entre que el navegador emite el evento y que este handler lo mide,
    // y el scroll anchoring del propio navegador mueve scrollTop por su cuenta.
    // Ese era el bug: el primer crecimiento marcaba atBottom=false y el
    // ResizeObserver de re-anclaje ya no volvía a actuar nunca, dejando el hilo
    // varado a mitad de camino. Solo un desplazamiento HACIA ARRIBA (scrollTop
    // que disminuye) significa que el agente se fue a leer historia.
    if (top < prevTop) atBottomRef.current = false;
  }, []);

  // (1) Apertura: antes del paint, y una sola vez por conversación.
  useLayoutEffect(() => {
    const convId = conversation?.id;
    if (!convId || loading) return;
    if (initialScrollDoneForRef.current === convId) return;
    const el = messagesContainerRef.current;
    if (!el) return;
    // Con el hilo todavía vacío no hay nada que anclar: se reintenta cuando
    // lleguen los mensajes (si no, marcaríamos la apertura como hecha y el
    // primer render con mensajes se trataría como "mensaje nuevo" → smooth).
    if (!messages?.length) return;
    el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    lastScrollTopRef.current = el.scrollTop;
    initialScrollDoneForRef.current = convId;
    // Se adopta el último mensaje como ya "autoscrolleado": el efecto de mensaje
    // nuevo (pasivo, corre después) no debe re-animar la apertura.
    lastMessageIdRef.current = messages[messages.length - 1].id;
  }, [conversation?.id, loading, messages]);

  // (2) Mensaje nuevo con el hilo ya abierto.
  useEffect(() => {
    // Solo se baja al fondo cuando cambia el ÚLTIMO mensaje del hilo. Si el
    // efecto reaccionara a searchOpen/searchTerm, cerrar el buscador arrastraría
    // al agente al final justo cuando acaba de saltar a un mensaje antiguo.
    if (initialScrollDoneForRef.current !== conversation?.id) return;
    const lastId = messages?.length ? messages[messages.length - 1].id : null;
    if (lastId === lastMessageIdRef.current) return;
    lastMessageIdRef.current = lastId;
    // Con la búsqueda activa el autoscroll al final pelearía con el salto a la
    // coincidencia: mientras se busca, el hilo no se mueve solo.
    if (searchOpen && searchTerm) return;
    // Leyendo historia más arriba: el mensaje nuevo NO lo arrastra al fondo.
    if (!atBottomRef.current) return;
    stickToBottom(true);
  }, [messages, searchOpen, searchTerm, conversation?.id, stickToBottom]);

  // (3) Re-ancla cuando el contenido cambia de alto después del salto (una foto
  // del hilo que termina de cargar, un <video> que resuelve su metadata…).
  // Sin esto, el salto de apertura se calcula con el hilo "corto" y la imagen
  // que carga tarde vuelve a dejar al agente a mitad de camino.
  useEffect(() => {
    if (loading) return undefined;
    const el = messagesContainerRef.current;
    const inner = messagesInnerRef.current;
    if (!el || !inner || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      if (!atBottomRef.current || searchGuardRef.current) return;
      // Siempre instantáneo: es una corrección de layout, no una navegación.
      el.scrollTop = el.scrollHeight;
      // El evento de scroll que dispara esta línea llega después; si no se
      // adelanta la referencia, se leería como un salto "hacia arriba" cuando
      // el navegador ya había anclado por su cuenta a una posición mayor.
      lastScrollTopRef.current = el.scrollTop;
    });
    ro.observe(inner);
    return () => ro.disconnect();
  }, [loading, conversation?.id]);

  // Salto + realce temporal al mensaje citado (click en el bloque de cita).
  const quoteFlashTimerRef = useRef(null);
  const jumpToMessage = useCallback((id) => {
    const el = messageRefs.current.get(id);
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
    setFlashId(id);
    clearTimeout(quoteFlashTimerRef.current);
    quoteFlashTimerRef.current = setTimeout(() => setFlashId(null), 1200);
  }, []);
  useEffect(() => () => clearTimeout(quoteFlashTimerRef.current), []);

  // Salto + realce temporal de la coincidencia activa.
  useEffect(() => {
    if (!searchOpen || !searchActiveId) return undefined;
    const el = messageRefs.current.get(searchActiveId);
    if (!el) return undefined;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    el.scrollIntoView({ block: "center", behavior: reduced ? "auto" : "smooth" });
    setFlashId(searchActiveId);
    const timer = setTimeout(() => setFlashId(null), 1200);
    return () => clearTimeout(timer);
  }, [searchOpen, searchActiveId, searchActive]);

  // ─ Auto-grow del composer sin layout forzado en cada tecla ────────────────
  // El patrón habitual (height:"auto" → leer scrollHeight → fijar height)
  // invalida el layout y luego lo fuerza a resolverse dentro del propio evento
  // input. Medido en el grupo de 4.945 mensajes: 1,5 ms por pulsación (p50) —
  // no es el cuello de botella del bug (ese era el re-render del hilo, ~197 ms)
  // pero es el único layout forzado que quedaba en el camino del teclado, y
  // sobra: con el layout limpio leer scrollHeight es gratis. Así que solo se
  // escribe estilo cuando el alto REALMENTE cambia, y el reset a "auto" se
  // reserva para las ediciones que pueden encoger la caja (con un height
  // explícito, scrollHeight nunca baja solo).
  const lastLenRef = useRef(0);
  const lastHeightRef = useRef(0);

  const autoGrow = useCallback((el, allowShrink) => {
    if (!el) return;
    if (allowShrink) el.style.height = "auto";
    const next = Math.min(el.scrollHeight, 160);
    if (next !== lastHeightRef.current) {
      el.style.height = `${next}px`;
      lastHeightRef.current = next;
    }
  }, []);

  // Devuelve el composer a una sola fila (tras enviar o cambiar de conversación).
  const resetComposerHeight = useCallback(() => {
    lastLenRef.current = 0;
    lastHeightRef.current = 0;
    if (inputRef.current) inputRef.current.style.height = "auto";
  }, []);

  useEffect(() => {
    setInput("");
    setError(null);
    setAgentPickerOpen(false);
    setCaseHistoryOpen(false);
    setFolderPickerOpen(false);
    setLightbox(null);
    setAttachedFile(null);
    setFilePreview(null);
    setQuickOpen(false);
    slashOpenedRef.current = false;
    setFlashId(null);
    // resetSearch (no closeSearch): al cambiar de conversación el foco pertenece
    // a la lista o al hilo nuevo, no al botón de buscar de la conversación anterior.
    resetSearch();
    setRenameOpen(false);
    setComposerMode("reply");
    setNoteMounted(false);
    setAiDraft(false);
    setLabelPickerOpen(false);
    setLabelPendingId(null);
    setMacrosOpen(false);
    // lastMessageIdRef NO se limpia acá: lo adopta el useLayoutEffect de
    // apertura (que corre ANTES que este efecto pasivo). Si se pusiera a null,
    // el efecto de "mensaje nuevo" trataría la apertura como mensaje entrante y
    // volvería a animar el scroll.
    atBottomRef.current = true;
    resetComposerHeight();
  }, [conversation?.id, resetSearch, resetComposerHeight]);

  const handleInputChange = useCallback(
    (e) => {
      const value = e.target.value;
      // Solo el caso "creció el texto" puede saltarse el reset a "auto": en
      // cualquier otra edición (borrar, reemplazar una selección, pegar más
      // corto) la caja podría tener que encoger y se recalcula como siempre.
      const grew = value.length > lastLenRef.current;
      lastLenRef.current = value.length;
      setInput(value);
      if (!value) setAiDraft(false);
      autoGrow(e.target, !grew);
    },
    [autoGrow],
  );

  // Inserta una respuesta rápida EN LA POSICIÓN DEL CARET (sustituyendo la
  // selección si la hay). La selección se lee antes del setState porque el
  // popover ya quitó el foco del textarea, pero el navegador conserva
  // selectionStart/End tras el blur.
  const insertAtCaret = useCallback((text) => {
    if (!text) return;
    const el = inputRef.current;
    const selStart = el ? el.selectionStart : null;
    const selEnd = el ? el.selectionEnd : null;
    setInput((prev) => {
      const start = selStart == null ? prev.length : selStart;
      const end = selEnd == null ? prev.length : selEnd;
      pendingCaret.current = start + text.length;
      return prev.slice(0, start) + text + prev.slice(end);
    });
  }, []);

  // Restaurar foco y caret tras la inserción (necesita el DOM ya actualizado).
  useEffect(() => {
    const pos = pendingCaret.current;
    if (pos == null) return;
    pendingCaret.current = null;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(pos, pos);
    // Inserción puntual (no una tecla): acá sí conviene el reset a "auto",
    // porque la respuesta rápida puede haber sustituido una selección larga.
    lastLenRef.current = el.value.length;
    autoGrow(el, true);
  }, [input, autoGrow]);

  // Cierre del popover de respuestas rápidas. Devuelve el foco al botón que lo
  // abrió (si no, cae al <body> y el usuario de teclado vuelve al principio del
  // documento) salvo cuando algo se inserta: ahí el destino es el composer.
  const closeQuick = useCallback(
    (reason, pendingQuery = "") => {
      setQuickOpen(false);
      const fromSlash = slashOpenedRef.current;
      slashOpenedRef.current = false;
      if (reason === "select") return; // insertAtCaret ya reenfoca el textarea
      if (reason === "escape" && fromSlash) {
        // Se repone la "/" (y lo tecleado tras ella) en el borrador: es la única
        // forma de escribir un mensaje que empiece por barra, p. ej. "/2 kilos".
        insertAtCaret(`/${pendingQuery}`);
        return;
      }
      quickBtnRef.current?.focus();
    },
    [insertAtCaret],
  );

  // Adjunta un archivo (validando tamaño y tipo) al composer. Reusado por el
  // input de archivos y por el pegado (Ctrl+V) de imágenes.
  const attachFile = useCallback(
    (file) => {
      if (!file) return false;
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(
          t("El archivo supera el límite de {{mb}} MB.").replace("{{mb}}", MAX_FILE_SIZE_MB),
        );
        return false;
      }
      if (!isAllowedMime(file.type)) {
        setError(t("Tipo de archivo no soportado."));
        return false;
      }
      setAttachedFile(file);
      setError(null);
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (ev) => setFilePreview(ev.target.result);
        reader.readAsDataURL(file);
      } else if (file.type.startsWith("audio/") || file.type.startsWith("video/")) {
        setFilePreview(URL.createObjectURL(file));
      } else {
        setFilePreview(null);
      }
      return true;
    },
    [t],
  );

  const handleFileSelect = useCallback(
    (e) => {
      attachFile(e.target.files?.[0]);
      e.target.value = "";
    },
    [attachFile],
  );

  // Pegar (Ctrl+V) una imagen del portapapeles la adjunta al composer, como en
  // WhatsApp Web. El texto se pega normal; solo interceptamos ítems de archivo.
  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind !== "file") continue;
        const blob = it.getAsFile();
        if (!blob) continue;
        // Los blobs pegados suelen venir con nombre genérico ("image.png") o sin
        // nombre; sintetizamos uno con extensión para el envío.
        const ext = (blob.type.split("/")[1] || "png").split("+")[0];
        const named =
          blob.name && blob.name !== "image.png"
            ? blob
            : new File([blob], `pegado-${Date.now()}.${ext}`, { type: blob.type });
        e.preventDefault();
        attachFile(named);
        return;
      }
    },
    [attachFile],
  );

  const handleRemoveFile = useCallback(() => {
    if (filePreview && filePreview.startsWith("blob:")) {
      URL.revokeObjectURL(filePreview);
    }
    setAttachedFile(null);
    setFilePreview(null);
  }, [filePreview]);

  useEffect(() => {
    return () => {
      if (filePreview && filePreview.startsWith("blob:")) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if ((!text && !attachedFile) || sending || !canCompose) return;

    setSending(true);
    setError(null);
    setInput("");
    const wasAiDraft = aiDraft;
    setAiDraft(false);
    setAttachedFile(null);
    setFilePreview(null);
    resetComposerHeight();

    try {
      await postHelpdeskMessage(replyTargetId, text, user, replyChannel, attachedFile);
    } catch (err) {
      // Mensaje contextual para AI cuando el backend rechaza
      // (probable: el log no tiene conversation_id todavía).
      if (isAIChannel) {
        setError(
          apiErrorMessage(
            err,
            t("No se pudo enviar el mensaje: responder directamente a un registro de IA está en construcción."),
          ),
        );
      } else {
        setError(apiErrorMessage(err, t("No se pudo enviar el mensaje.")));
      }
      // Restauramos el texto para no perderlo
      setInput(text);
      setAiDraft(wasAiDraft);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [
    input,
    aiDraft,
    attachedFile,
    sending,
    canCompose,
    replyTargetId,
    replyChannel,
    isAIChannel,
    user,
    t,
    resetComposerHeight,
  ]);

  // Envía la nota de voz grabada. content_type: voice_note → el backend la rutea
  // al endpoint ptt de Evolution (burbuja de nota de voz, no un archivo de audio).
  const sendVoiceNote = useCallback(
    async (file) => {
      if (!file || sending || !canCompose) return;
      setSending(true);
      setError(null);
      try {
        await postHelpdeskMessage(replyTargetId, "", user, replyChannel, file, "voice_note");
        setVoiceActive(false);
      } catch (err) {
        setError(apiErrorMessage(err, t("No se pudo enviar la nota de voz.")));
      } finally {
        setSending(false);
      }
    },
    [sending, canCompose, replyTargetId, replyChannel, user, t],
  );

  // Al cambiar de conversación, cerrar cualquier grabación en curso.
  useEffect(() => {
    setVoiceActive(false);
  }, [replyTargetId]);

  const handleKeyDown = useCallback(
    (e) => {
      // "/" en un borrador vacío abre las respuestas rápidas (estilo Slack). La
      // barra no se inserta —el filtrado ocurre dentro del popover—, pero se
      // recuerda el origen: si el agente descarta el popover con Esc, closeQuick
      // repone la "/" para que pueda escribir mensajes que empiecen por barra.
      if (
        e.key === "/" &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        !input &&
        !quickOpen
      ) {
        e.preventDefault();
        slashOpenedRef.current = true;
        setQuickOpen(true);
        return;
      }
      // Ctrl/Cmd+Enter siempre envía. Enter (sin Shift) envía si no hay archivo.
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSend();
        return;
      }
      if (e.key === "Enter" && !e.shiftKey && !attachedFile) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, attachedFile, input, quickOpen],
  );

  const handleAssign = useCallback(
    async (agentId = null) => {
      setActionLoading(true);
      setError(null);
      try {
        const updated = await assignConversation(
          conversation.id,
          agentId,
          user,
          conversation.channel,
        );
        onUpdated?.(updated);
      } catch (err) {
        // Texto del backend ('Conversacion no encontrada', 'Se requiere un
        // operador logístico en la sesión.') o el fallback traducido.
        setError(apiErrorMessage(err, t("No se pudo asignar.")));
      } finally {
        setActionLoading(false);
        setAgentPickerOpen(false);
      }
    },
    [conversation?.id, conversation?.channel, user, onUpdated, t],
  );

  // Tomar una atención atendida por IA: promueve el log a conversación real y
  // la asigna al agente. El bridge devuelve la conversación creada (channel
  // portal, assigned); onTakeover la selecciona en el inbox — su id es NUEVO
  // (distinto del id del log), así que no basta con onUpdated, que casa por id.
  const handleTakeover = useCallback(async () => {
    setActionLoading(true);
    setError(null);
    try {
      const conv = await takeoverAiLog(conversation.id);
      const next = conv?.conversation || conv;
      if (next?.id) onTakeover?.({ ...next, channel: next.channel || "portal" });
    } catch (err) {
      // "Falta desplegar el bridge" solo para un 404/405 SIN mensaje del backend
      // (ruta ausente). Un 404 con texto de Nest ('Log no encontrado') es un
      // error real: se muestra ese texto o el fallback traducido.
      if (isEndpointMissing(err) && !err?.serverMessage) {
        setError(
          t("Tomar atenciones de IA todavía no está disponible: falta desplegar el bridge."),
        );
      } else {
        setError(apiErrorMessage(err, t("No se pudo tomar la atención.")));
      }
    } finally {
      setActionLoading(false);
    }
  }, [conversation?.id, onTakeover, t]);

  const handleResolve = useCallback(async () => {
    setActionLoading(true);
    setError(null);
    try {
      const updated = await resolveConversation(conversation.id, user, conversation.channel);
      onUpdated?.(updated);
    } catch (err) {
      setError(apiErrorMessage(err, t("No se pudo cerrar.")));
    } finally {
      setActionLoading(false);
      setShowConfirmResolve(false);
    }
  }, [conversation?.id, conversation?.channel, user, onUpdated, t]);

  const closeRename = useCallback(() => setRenameOpen(false), []);

  // Mover a carpeta: el padre hace el PATCH + refetch de carpetas; acá solo se
  // administra el loading y el error inline (mismo canal de error que el resto
  // de acciones del detalle).
  const handlePickFolder = useCallback(
    async (folderId) => {
      setFolderPickerOpen(false);
      if (!onMoveToFolder) return;
      setActionLoading(true);
      setError(null);
      try {
        await onMoveToFolder(conversation, folderId);
      } catch (err) {
        setError(err?.message || t("No se pudo mover la conversación."));
      } finally {
        setActionLoading(false);
      }
    },
    [onMoveToFolder, conversation, t],
  );

  const closeFolderPicker = useCallback(() => setFolderPickerOpen(false), []);

  // ─ Etiquetas: cada clic del selector aplica el cambio en el acto ─
  const handleToggleLabel = useCallback(
    async (labelId, add) => {
      if (!conversation?.id) return;
      setLabelPendingId(labelId);
      setError(null);
      try {
        const updated = await setConversationLabels(
          conversation.id,
          add ? { add: [labelId] } : { remove: [labelId] },
        );
        // Idempotente con el status_change que emite el backend.
        if (updated?.id) onUpdated?.(updated);
      } catch (err) {
        setError(workflowErrorMessage(err, t("No se pudieron actualizar las etiquetas.")));
      } finally {
        setLabelPendingId(null);
      }
    },
    [conversation?.id, onUpdated, t],
  );
  const closeLabelPicker = useCallback(() => setLabelPickerOpen(false), []);
  const closeMacros = useCallback(() => setMacrosOpen(false), []);

  // ─ Composer: cambio de pestaña ─
  const changeComposerMode = useCallback((next) => {
    setComposerMode(next);
    if (next === "note") {
      setNoteMounted(true);
      setQuickOpen(false);
      slashOpenedRef.current = false;
    }
  }, []);

  // ─ Copiloto ─
  // Espejo del borrador para decidir, DESPUÉS del await, si hay que pedir
  // confirmación antes de reemplazarlo (el agente pudo seguir escribiendo).
  const inputMirrorRef = useRef(input);
  useLayoutEffect(() => {
    inputMirrorRef.current = input;
  });
  const handleCopilotSummary = useCallback(() => {
    askCopilotFor("summary");
  }, [askCopilotFor]);
  const handleCopilotReply = useCallback(async () => {
    const draft = await askCopilotFor("reply");
    if (!draft) return;
    const current = inputMirrorRef.current.trim();
    if (
      current &&
      current !== draft &&
      !window.confirm(t("¿Reemplazar lo que escribiste por el borrador de la IA?"))
    ) {
      return;
    }
    setComposerMode("reply");
    // El efecto del caret enfoca el editor, deja el cursor al final y ajusta
    // el alto. Nada se envía: la persona revisa y envía a mano.
    pendingCaret.current = draft.length;
    setInput(draft);
    setAiDraft(true);
  }, [askCopilotFor, t]);

  // ─ Avisos de escritorio (preferencia del navegador del agente) ─
  const { enabled: notifyEnabled, supported: notifySupported, setEnabled: setNotifyEnabled } =
    notifications;
  const toggleDesktopNotifications = useCallback(async () => {
    const result = await setNotifyEnabled(!notifyEnabled);
    if (result === "on") toast.success(t("Avisos de escritorio activados"));
    else if (result === "off") toast.info(t("Avisos de escritorio desactivados"));
    else if (result === "denied") {
      toast.error(
        t("El navegador bloqueó los avisos. Permítelos en la configuración del sitio y vuelve a intentarlo."),
      );
    } else toast.error(t("Este navegador no admite avisos de escritorio."));
  }, [notifyEnabled, setNotifyEnabled, t]);
  const closeCaseHistory = useCallback(() => setCaseHistoryOpen(false), []);

  // Índices caso → ticket / caso → entrada sellada. La dependencia es la HUELLA
  // del ciclo de casos, no el objeto `conversation` (que cambia de identidad con
  // cada evento de asignación/estado): si dependiera del objeto, el hilo
  // memoizado se re-renderizaría entero en cada resolve.
  const caseIndex = useMemo(
    () => caseIndexFor(conversation),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [caseIndexKey(conversation)],
  );

  if (!conversation) return null;

  // Mismo nombre que pinta la fila de la lista: el canal interno no tiene
  // contacto (ni contact_name ni external_id, y su user_id es el ObjectId de
  // quien escribió), así que ahí se muestra "Equipo interno" en vez de un id.
  const headerName = conversationDisplayName(conversation, getDisplayName, t);

  // Chip de asignación en la línea de meta. assigned_by / assigned_at los está
  // añadiendo el backend en paralelo: si aún no existen, se degrada al chip solo.
  const assignedName = conversation.assigned_to
    ? (getDisplayName && getDisplayName(conversation.assigned_to)) || conversation.assigned_to
    : null;
  const assignedTooltip = (() => {
    if (!conversation.assigned_by || conversation.assigned_by === conversation.assigned_to) {
      return null;
    }
    const byName =
      (getDisplayName && getDisplayName(conversation.assigned_by)) || conversation.assigned_by;
    if (conversation.assigned_at) {
      return t("Asignada por {{nombre}} · {{fecha}}")
        .replace("{{nombre}}", byName)
        .replace("{{fecha}}", dayjs(conversation.assigned_at).format("DD-MM HH:mm"));
    }
    return t("Asignada por {{nombre}}").replace("{{nombre}}", byName);
  })();

  // Nombre actual del grupo: si contact_name sigue siendo el JID crudo, no hay
  // nombre puesto todavía (el JID se muestra como placeholder, no como valor).
  const isGroup = !!conversation.is_group;
  const currentGroupName =
    conversation.contact_name && conversation.contact_name !== conversation.external_id
      ? conversation.contact_name
      : "";

  const headerInitial = (headerName?.trim()?.[0] || "?").toUpperCase();
  const headerAvatarTone = avatarTone(
    conversation.user_id || conversation.external_id || conversation.id
  );

  // Carpeta de la conversación (si el índice la conoce; una carpeta recién
  // borrada puede dejar el folder_id huérfano hasta el próximo refetch).
  const headerFolder =
    (foldersById && conversation.folder_id && foldersById[conversation.folder_id]) || null;

  // Etiquetas resueltas contra el catálogo (sin chip para ids huérfanos).
  const headerLabels = supportsWorkflow
    ? (Array.isArray(conversation.labels) ? conversation.labels : [])
        .map((id) => labelsById[id])
        .filter(Boolean)
    : [];

  // Acción primaria contextual del header (una sola, sin ruido)
  const renderPrimaryAction = () => {
    if (isResolved) return null;
    // D3 · Los tres botones de acción del hilo («Tomar caso» ×2 y «Cerrar
    // caso») vuelven a `h-8 px-2.5`, que es el baseline. El intento anterior
    // escribía `h-11 md:h-8 px-3 md:px-2.5` y `.px-3` es utilidad de BOOTSTRAP
    // con `!important` (App.js:9 lo carga después de Tailwind): la variante
    // `md:px-2.5` no gana nunca y el padding de escritorio pasaba de 9 a
    // 14,4px por lado. El objetivo táctil de teléfono lo da ahora
    // `hd-touch-btn` (helpdeskMobile.scss, bajo `max-width: 767.98px`).
    if (isAIChannel) {
      // Atención del portal atendida por IA: el agente la toma (takeover) y
      // pasa a atenderla como conversación real; la IA deja de responderle al
      // cliente. Antes esto no existía y escribir daba 404 "Conversation not
      // found" (el log no es una conversación).
      return (
        <button
          type="button"
          onClick={handleTakeover}
          disabled={actionLoading}
          className="hd-brand-solid hd-touch-btn inline-flex items-center gap-[6px] h-[32px] px-[12px] rounded-lg text-[13px] font-semibold bg-brand-500 text-bg hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 transition-colors"
        >
          <UserPlus className="w-3.5 h-3.5" aria-hidden="true" />
          {/* Icon-only below md: with the label the button ate the header
              row and the contact name collapsed to nothing at 390px. The
              text stays in the DOM as the accessible name. */}
          <span className="max-md:sr-only">{t("Tomar caso")}</span>
        </button>
      );
    }
    if (!isAIChannel && isPending) {
      return (
        <button
          type="button"
          onClick={() => handleAssign(null)}
          disabled={actionLoading}
          className="hd-brand-solid hd-touch-btn inline-flex items-center gap-[6px] h-[32px] px-[12px] rounded-lg text-[13px] font-semibold bg-brand-500 text-bg hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 transition-colors"
        >
          <UserPlus className="w-3.5 h-3.5" aria-hidden="true" />
          {/* Icon-only below md: with the label the button ate the header
              row and the contact name collapsed to nothing at 390px. The
              text stays in the DOM as the accessible name. */}
          <span className="max-md:sr-only">{t("Tomar caso")}</span>
        </button>
      );
    }
    if (!isAIChannel && conversation.status === "assigned") {
      return (
        <button
          type="button"
          onClick={() => setShowConfirmResolve(true)}
          disabled={actionLoading}
          /* Second visual pass: outlined neutral button with a green check.
             The solid green block competed with "Tomar caso" and the agent
             bubbles; closing is frequent but not the one accent. */
          className="hd-touch-btn inline-flex items-center gap-[6px] h-[32px] px-[12px] rounded-lg text-[13px] font-semibold border border-border bg-bg text-fg hover:bg-bg-muted disabled:opacity-50 transition-colors"
        >
          <CheckCircle2 className="w-3.5 h-3.5 text-status-success" aria-hidden="true" />
          {/* Icon-only below md (same reason as "Tomar caso"). */}
          <span className="max-md:sr-only">{t("Cerrar caso")}</span>
        </button>
      );
    }
    return null;
  };

  // Acciones secundarias del menú overflow
  const overflowActions = [];
  // Fijar va primero: es la acción de fila más usada y no depende del estado
  // del caso (el resto son de triage o de administración del grupo).
  if (onTogglePin) {
    overflowActions.push({
      key: "pin",
      label: pinned ? t("Desfijar chat") : t("Fijar chat"),
      icon: pinned ? PinOff : Pin,
      onClick: () => {
        setMenuOpen(false);
        onTogglePin(conversation);
      },
    });
  }
  if (isGroup) {
    overflowActions.push({
      key: "renameGroup",
      label: t("Renombrar grupo"),
      icon: Pencil,
      onClick: () => {
        setMenuOpen(false);
        setRenameOpen(true);
      },
    });
  }
  if (isSupervisor && !isAIChannel && isPending) {
    // Asignación directa a un tercero sin pasar por "Tomar caso".
    overflowActions.push({
      key: "assignTo",
      label: t("Asignar a…"),
      icon: UserCheck,
      onClick: () => {
        setMenuOpen(false);
        setAgentPickerOpen(true);
      },
    });
  }
  if (isSupervisor && !isAIChannel && conversation.status === "assigned" && !isResolved) {
    overflowActions.push({
      key: "reassign",
      label: t("Reasignar"),
      icon: ArrowLeftRight,
      onClick: () => {
        setMenuOpen(false);
        setAgentPickerOpen(true);
      },
    });
  }
  if (!isAIChannel && onMoveToFolder) {
    // Mover a carpeta vale SIEMPRE en canales reales, incluso resuelta: la
    // carpeta es organización de bandeja, no parte del ciclo de triage.
    overflowActions.push({
      key: "moveToFolder",
      label: conversation.folder_id ? t("Cambiar de carpeta…") : t("Mover a carpeta…"),
      icon: FolderInput,
      disabled: actionLoading,
      onClick: () => {
        setMenuOpen(false);
        setFolderPickerOpen(true);
      },
    });
  }
  const canRunMacros = supportsWorkflow && !isResolved;
  if (canRunMacros && macrosInMenu) {
    // Sin sitio para otro ícono en el header (<1440px): las macros van al menú.
    overflowActions.push({
      key: "macros",
      label: t("Macros…"),
      icon: Wand2,
      disabled: actionLoading,
      onClick: () => {
        setMenuOpen(false);
        setMacrosOpen(true);
      },
    });
  }
  if (notifySupported) {
    overflowActions.push({
      key: "desktopNotifications",
      label: t("Avisos de escritorio"),
      icon: notifyEnabled ? Bell : BellOff,
      role: "menuitemcheckbox",
      checked: notifyEnabled,
      onClick: () => {
        setMenuOpen(false);
        toggleDesktopNotifications();
      },
    });
  }
  // El panel de contacto ya NO vive en el menú overflow: se abre/cierra
  // clickeando el bloque avatar+nombre del header (affordance directa).
  // En desktop wide el padre controla la 3ra columna (onToggleContactPanel);
  // sin ese callback, el mismo click abre el ContactPanel en modo overlay.
  const contactOpen = onToggleContactPanel ? !!contactPanelOpen : sidePanelOpen;
  const handleContactBlockClick = () => {
    if (onToggleContactPanel) onToggleContactPanel();
    else setSidePanelOpen((p) => !p);
  };
  const primaryAction = renderPrimaryAction();

  return (
    <div ref={rootRef} className="flex-1 flex flex-col min-h-0 bg-bg">
      {/* ── Header ─────────────────────────────────────────────────────────
          Second visual pass. Row 1: identity (avatar + name + one quiet
          subline: channel · phone) and, right-aligned, the icon tools plus the
          single primary action. Row 2: case/status and plain-text meta in one
          non-wrapping group (truncates), then the labels group; the two groups
          wrap as units, so at wide widths they share one line. */}
      <header className="px-[16px] pt-[10px] pb-[10px] border-b border-border/60 bg-bg shrink-0">
        <div className="flex items-center gap-[10px] min-w-0">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label={t("Volver a la lista")}
              className="inline-flex w-11 h-11 md:w-8 md:h-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors -ml-[8px] shrink-0"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            </button>
          )}

          {/* Bloque de identidad CLICKEABLE: abre/cierra el panel de contacto.
              Un <button> solo admite phrasing content, por eso todo va en
              <span>. El chevron rota como affordance de estado. */}
          <button
            type="button"
            onClick={handleContactBlockClick}
            aria-expanded={contactOpen}
            {...(onToggleContactPanel
              ? { "aria-controls": "helpdesk-contact-panel" }
              : { "aria-haspopup": "dialog" })}
            aria-label={t("Ver detalles del contacto")}
            title={headerName}
            className="flex-1 min-w-0 flex items-center gap-[10px] text-left rounded-lg -mx-[6px] px-[6px] py-[2px] min-h-[44px] md:min-h-0 hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors group/contact"
          >
            <span className="shrink-0 relative">
              {isAIChannel ? (
                <span className="w-[36px] h-[36px] rounded-full bg-bg-subtle text-fg-muted flex items-center justify-center">
                  <Bot className="w-[18px] h-[18px]" aria-hidden="true" />
                </span>
              ) : conversation.is_group ? (
                <span className={cn("w-[36px] h-[36px] rounded-full flex items-center justify-center", headerAvatarTone)}>
                  <Users className="w-[18px] h-[18px]" aria-hidden="true" />
                </span>
              ) : (
                <ContactAvatar
                  src={conversation.contact_profile_pic}
                  initial={headerInitial}
                  toneClass={headerAvatarTone}
                  className="w-[36px] h-[36px] text-[13px]"
                  onImageClick={() =>
                    setLightbox({
                      url: conversation.contact_profile_pic,
                      caption: headerName,
                      profile: true,
                    })
                  }
                />
              )}
            </span>
            <span className="min-w-0 flex flex-col">
              <span className="flex items-center gap-[4px] min-w-0">
                <span className="min-w-0 text-[15px] leading-[20px] font-semibold text-fg truncate">
                  {headerName}
                </span>
                {/* El estado accesible lo da el ítem del menú ("Desfijar
                    chat"); acá el glifo es solo la señal visual. En teléfono
                    se omite: a 390px le quitaba al nombre el poco ancho que
                    tiene, y la fila de la lista ya muestra la chincheta. */}
                {pinned && (
                  <Pin
                    className="max-md:hidden w-3.5 h-3.5 shrink-0 rotate-45 text-fg-muted"
                    aria-hidden="true"
                  />
                )}
                <ChevronDown
                  aria-hidden="true"
                  className={cn(
                    "w-4 h-4 shrink-0 text-fg-muted transition-transform motion-reduce:transition-none",
                    contactOpen && "rotate-180",
                  )}
                />
              </span>
              {/* Subline: solo lo corto y estable (canal · teléfono). El
                  canal interno no tiene contacto ni teléfono. */}
              <span className="flex items-center gap-[6px] min-w-0 text-[12px] leading-[16px] text-fg-muted">
                {isGroup && (
                  <span className="inline-flex items-center gap-[4px] shrink-0">
                    <Users className="w-3 h-3" aria-hidden="true" />
                    {t("Grupo")}
                  </span>
                )}
                {isGroup && conversation.channel && (
                  <span className="text-fg-subtle" aria-hidden="true">·</span>
                )}
                {conversation.channel && (
                  <span
                    className="shrink-0"
                    title={
                      CHANNEL_TITLES[conversation.channel]
                        ? t(CHANNEL_TITLES[conversation.channel])
                        : undefined
                    }
                  >
                    {t(CHANNEL_LABELS[conversation.channel] ?? conversation.channel)}
                  </span>
                )}
                {!isInternalChannel && conversation.contact_phone && (
                  <>
                    <span className="text-fg-subtle" aria-hidden="true">·</span>
                    <span className="min-w-0 truncate tabular-nums">
                      {conversation.contact_phone}
                    </span>
                  </>
                )}
              </span>
            </span>
          </button>

          {/* Herramientas de ícono + acción primaria (a la derecha del todo) */}
          <div className="relative flex items-center gap-[2px] shrink-0">
            {canRunMacros && !macrosInMenu && (
              <MacrosButton
                ref={macrosBtnRef}
                open={macrosOpen}
                onClick={() => setMacrosOpen((p) => !p)}
                disabled={actionLoading}
              />
            )}
            <button
              ref={searchBtnRef}
              type="button"
              onClick={searchOpen ? closeSearch : search.openSearch}
              aria-expanded={searchOpen}
              aria-controls={THREAD_SEARCH_ID}
              aria-label={t("Buscar en la conversación")}
              title={t("Buscar en la conversación (Ctrl/Cmd+F)")}
              className={cn(
                // 44px táctil / 32px desde md, como el resto de controles del helpdesk.
                "inline-flex w-11 h-11 md:w-8 md:h-8 items-center justify-center rounded-md transition-colors",
                searchOpen
                  ? "bg-bg-subtle text-fg"
                  : "text-fg-muted hover:bg-bg-muted hover:text-fg",
              )}
            >
              <Search className="w-4 h-4" aria-hidden="true" />
            </button>
            {overflowActions.length > 0 && (
              <div className="relative">
                <button
                  ref={menuBtnRef}
                  type="button"
                  onClick={() => setMenuOpen((p) => !p)}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-label={t("Más acciones")}
                  className="inline-flex w-11 h-11 md:w-8 md:h-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
                >
                  <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
                </button>
                {menuOpen && (
                  <div
                    ref={menuRef}
                    role="menu"
                    className="absolute right-0 top-full mt-1 min-w-[180px] z-30 bg-bg border border-border rounded-lg shadow-elevated py-1 animate-scale-in origin-top-right motion-reduce:animate-none"
                  >
                    {overflowActions.map((a) => (
                      <button
                        key={a.key}
                        type="button"
                        role={a.role || "menuitem"}
                        aria-checked={a.role === "menuitemcheckbox" ? !!a.checked : undefined}
                        onClick={a.onClick}
                        disabled={a.disabled || undefined}
                        className="w-full flex items-center gap-2 px-3 h-8 text-[13px] text-fg hover:bg-bg-muted text-left disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <a.icon className="w-3.5 h-3.5 text-fg-muted shrink-0" aria-hidden="true" />
                        <span className="flex-1">{a.label}</span>
                        {a.role === "menuitemcheckbox" && (
                          <span
                            className={cn(
                              "text-[11px] font-medium",
                              a.checked ? "text-status-success" : "text-fg-muted",
                            )}
                          >
                            {a.checked ? t("Activados") : t("Apagados")}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {primaryAction && (
              <>
                <span className="hidden md:block w-px h-[20px] mx-[6px] bg-border" aria-hidden="true" />
                {primaryAction}
              </>
            )}
            {agentPickerOpen && (
              <AgentPicker
                onSelect={(agentId) => handleAssign(agentId)}
                onClose={() => setAgentPickerOpen(false)}
              />
            )}
            {folderPickerOpen && (
              <FolderPicker
                folders={folders || []}
                currentFolderId={conversation.folder_id || null}
                onPick={handlePickFolder}
                onClose={closeFolderPicker}
              />
            )}
            {canRunMacros && (
              <MacrosControl
                open={macrosOpen}
                onClose={closeMacros}
                conversation={conversation}
                foldersById={foldersById}
                getDisplayName={getDisplayName}
                onUpdated={onUpdated}
                onRefresh={onRefreshConversation}
                anchorRef={macrosInMenu ? menuBtnRef : macrosBtnRef}
                onBusyChange={setActionLoading}
              />
            )}
          </div>
        </div>

        {/* Fila 2: un solo flujo que envuelve por ítem (nunca a mitad de
            uno): caso, estado/asignado, excepciones, comercio, envío,
            carpeta y etiquetas. Sin "·": cada dato lleva su ícono, así una
            línea cortada no deja separadores colgando. A 1440px+ cabe en una
            línea; a 1280px pasa a dos sin truncar nada. `relative` en la fila:
            el LabelPicker (w-64, left-0) se ancla a su borde izquierdo y no
            desborda la columna. Sangrada para alinear con el nombre. En
            teléfono la fila no envuelve: es UNA línea con scroll horizontal
            (antes eran tres líneas de datos sobre el hilo); el picker vive
            fuera del contenedor con scroll para no quedar recortado. */}
        <div className="relative mt-[8px] min-w-0">
        <div
          className={cn(
            "flex items-center gap-x-[12px] gap-y-[6px] min-w-0 text-[12px] leading-[16px] text-fg-muted",
            "md:flex-wrap max-md:overflow-x-auto max-md:-mx-[16px] max-md:px-[16px] max-md:[scrollbar-width:none] max-md:[&>*]:shrink-0",
            !onBack && "md:pl-[46px]",
          )}
        >
          {/* Chip de caso: botón (abre el historial). Los logs de IA no
              tienen ciclo de casos. */}
          {!isAIChannel && (
            <span className="relative inline-flex shrink-0" title={conversation.id}>
              <button
                type="button"
                onClick={() => setCaseHistoryOpen((p) => !p)}
                aria-haspopup="dialog"
                aria-expanded={caseHistoryOpen}
                title={t("Ver historial de casos")}
                // The visible chip is the inner span: on touch screens the
                // global 44px min-height of <button> would otherwise inflate
                // the grey box itself; the button keeps the full hit area.
                className="group/case inline-flex items-center rounded-md shrink-0 tabular-nums whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <span className="inline-flex items-center gap-[4px] h-[22px] px-[8px] rounded-md bg-bg-subtle text-fg font-medium group-hover/case:bg-bg-muted transition-colors">
                  <History className="w-3 h-3 text-fg-muted" aria-hidden="true" />
                  {caseLabel(conversation.case_number || 1, conversation.ticket_number, t)}
                </span>
              </button>
              {caseHistoryOpen && (
                <CaseHistoryPopover
                  conversation={conversation}
                  getDisplayName={getDisplayName}
                  onClose={closeCaseHistory}
                />
              )}
            </span>
          )}
          {/* Estado: pastilla visible (Pendiente / Asignado / Resuelto) con el
              agente asignado en texto al lado. Abierto/Cerrado queda en el
              tooltip y para lectores de pantalla. */}
          <span
            className="inline-flex items-center gap-[6px] min-w-0 max-w-full text-fg whitespace-nowrap"
            title={
              conversation.status === "assigned" && assignedName
                ? assignedTooltip ||
                  t("Asignada a {{agente}}").replace("{{agente}}", assignedName)
                : `${t(conversation.status === "resolved" ? "Cerrado" : "Abierto")} · ${t(
                    STATUS_LABELS[conversation.status] ?? conversation.status,
                  )}`
            }
          >
            <span
              className={cn(
                "inline-flex items-center gap-[5px] h-[20px] px-[8px] rounded-full text-[11px] font-medium shrink-0",
                STATUS_TONE[conversation.status] ?? "bg-bg-muted text-fg-muted",
              )}
            >
              <span className="w-[6px] h-[6px] rounded-full shrink-0 bg-current" aria-hidden="true" />
              {!isAIChannel && (
                <span className="sr-only">
                  {t(conversation.status === "resolved" ? "Cerrado" : "Abierto")}
                </span>
              )}
              {t(STATUS_LABELS[conversation.status] ?? conversation.status)}
            </span>
            {conversation.assigned_to && assignedName && (
              <span className="inline-flex items-center gap-[4px] min-w-0 text-fg-muted">
                <UserCheck className="w-3 h-3 shrink-0" aria-hidden="true" />
                <span className="truncate">{assignedName}</span>
              </span>
            )}
          </span>
          {/* Excepciones del caso: texto teñido, no pills. */}
          {conversation.sla_breached && (
            <span className="inline-flex items-center gap-[4px] shrink-0 font-medium text-status-error whitespace-nowrap">
              <AlertTriangle className="w-3 h-3" aria-hidden="true" />
              {t("SLA vencido")}
            </span>
          )}
          {conversation.transfer_reason === "user_request" && (
            <span className="inline-flex items-center gap-[4px] shrink-0 font-medium text-status-info whitespace-nowrap">
              <UserRound className="w-3 h-3" aria-hidden="true" />
              {t("Solicitó humano")}
            </span>
          )}
          {conversation.transfer_reason === "ai_failure" && (
            <span className="inline-flex items-center gap-[4px] shrink-0 font-medium text-status-error whitespace-nowrap">
              <AlertTriangle className="w-3 h-3" aria-hidden="true" />
              {t("Fallo de IA")}
            </span>
          )}
          {!isInternalChannel && conversation.effective_commerce_name && (
            <CommerceChip name={conversation.effective_commerce_name} t={t} />
          )}
          {/* El package_id NO aparece en ningún otro punto de la UI: se queda
              acá para no perder el vínculo conversación ↔ envío. */}
          {conversation.package_id && (
            <span
              className="inline-flex items-center gap-[4px] min-w-0 max-w-full whitespace-nowrap"
              title={t("Paquete {{id}}").replace("{{id}}", conversation.package_id)}
            >
              <Package className="w-3 h-3 shrink-0" aria-hidden="true" />
              <span className="font-mono truncate">{conversation.package_id}</span>
            </span>
          )}
          {headerFolder && (
            <span
              className="inline-flex items-center gap-[4px] min-w-0 max-w-[180px] whitespace-nowrap"
              title={t("Carpeta: {{nombre}}").replace("{{nombre}}", headerFolder.name)}
            >
              <span
                className={cn(
                  "w-[6px] h-[6px] rounded-[2px] shrink-0",
                  (FOLDER_COLOR_STYLES[headerFolder.color] ||
                    FOLDER_COLOR_STYLES[DEFAULT_FOLDER_COLOR]).dot,
                )}
                aria-hidden="true"
              />
              <Folder className="w-3 h-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{headerFolder.name}</span>
            </span>
          )}

          {/* Etiquetas de la conversación (chips) + editar. Un id que ya no
              está en el catálogo (etiqueta borrada) no pinta chip. */}
          {supportsWorkflow && (
            <span className="inline-flex items-center gap-[4px] md:flex-wrap min-w-0 md:max-w-full">
              {headerLabels.map((label) => (
                <LabelChip key={label.id} label={label} />
              ))}
              <span className="inline-flex shrink-0">
                <button
                  ref={labelBtnRef}
                  type="button"
                  onClick={() => setLabelPickerOpen((p) => !p)}
                  aria-haspopup="dialog"
                  aria-expanded={labelPickerOpen}
                  aria-label={t("Editar etiquetas")}
                  title={t("Editar etiquetas")}
                  // Visible pill = inner span (same pattern as the case chip):
                  // the global 44px touch min-height must not inflate it.
                  className={cn(
                    "group/lbl inline-flex items-center justify-center rounded-full whitespace-nowrap",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                    !headerLabels.length && "-ml-[8px]",
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center gap-[4px] h-[20px] rounded-full text-[11px] font-medium transition-colors",
                      headerLabels.length ? "w-[20px]" : "px-[8px]",
                      labelPickerOpen
                        ? "bg-bg-subtle text-fg"
                        : "text-fg-muted group-hover/lbl:bg-bg-subtle group-hover/lbl:text-fg",
                    )}
                  >
                    {headerLabels.length ? (
                      <Plus className="w-3 h-3" aria-hidden="true" />
                    ) : (
                      <>
                        <Tag className="w-3 h-3" aria-hidden="true" />
                        {t("Etiquetar")}
                      </>
                    )}
                  </span>
                </button>
              </span>
            </span>
          )}
        </div>
          {/* El picker vive en el envoltorio de la fila (no dentro del botón
              ni del contenedor con scroll) para anclarse a su borde izquierdo
              sin recortarse. */}
          {supportsWorkflow && labelPickerOpen && (
            <LabelPicker
              selectedIds={conversation.labels || []}
              onToggle={handleToggleLabel}
              pendingId={labelPendingId}
              onClose={closeLabelPicker}
              anchorRef={labelBtnRef}
            />
          )}
        </div>

        {searchOpen && (
          <ThreadSearch
            query={search.query}
            onQueryChange={search.setQuery}
            total={search.total}
            ordinal={search.ordinal}
            onNext={search.goNext}
            onPrev={search.goPrev}
            onClose={closeSearch}
            inputRef={search.inputRef}
          />
        )}
      </header>

      {/* Acciones del contenedor: fuera del menú overflow a propósito, porque
          son las esenciales de esa vista y no deben quedar escondidas. */}
      {contextBar ? (
        <div className="px-4 py-1.5 border-b border-border bg-bg flex items-center gap-2 flex-wrap">
          {contextBar}
        </div>
      ) : null}

      <WindowBanner conversation={conversation} />
      <ByoAiBanner conversation={conversation} />
      <AIContextBanner conversation={conversation} />

      {/* ── Mensajes ───────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center text-sm text-fg-muted" aria-live="polite">
          <span className="inline-block w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {t("Cargando mensajes…")}
        </div>
      ) : (
        <div
          ref={messagesContainerRef}
          onScroll={handleThreadScroll}
          aria-live="polite"
          aria-label={t("Mensajes de la conversación")}
          className="flex-1 overflow-y-auto px-[20px] pt-[8px] pb-[20px] bg-bg-muted/40"
        >
          {/* Wrapper sin estilos: solo existe para que el ResizeObserver pueda
              medir el ALTO DEL CONTENIDO (el contenedor scrolleable no cambia de
              tamaño cuando una imagen del hilo termina de cargar). */}
          <div ref={messagesInnerRef}>
          {messages.length === 0 && (
            <div className="text-center text-sm text-fg-muted py-12">
              {t("No hay mensajes todavía.")}
            </div>
          )}

          <MessageThread
            messages={messages}
            isGroup={isGroup}
            getDisplayName={getDisplayName}
            currentUserId={user?.id}
            caseIndex={caseIndex}
            isWhatsAppChannel={isWhatsAppChannel}
            searchTerm={searchTerm}
            searchActive={searchActive}
            searchActiveId={searchActiveId}
            matchBaseFor={matchBaseFor}
            flashId={flashId}
            registerRef={registerMessageRef}
            onImageOpen={setLightbox}
            contactLabel={headerName}
            onQuoteJump={jumpToMessage}
            onOpenIncident={openIncident}
          />

          <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* Error de carga de la conversación: mismo canal visual que el error de
          envío, pero separado porque la acción que lo resuelve es otra
          (reintentar la carga, no corregir el texto). */}
      {detailError && !loading && (
        <div
          role="alert"
          className="px-4 py-2 text-xs bg-status-error-soft text-status-error border-t border-status-error/20 flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span className="flex-1">{detailError}</span>
          {onRetryDetail && (
            <button
              type="button"
              onClick={onRetryDetail}
              className="px-2 py-0.5 rounded font-medium underline hover:bg-status-error/10"
            >
              {t("Reintentar")}
            </button>
          )}
        </div>
      )}

      {/* Error de envío */}
      {error && (
        <div
          role="alert"
          className="px-4 py-2 text-xs bg-status-error-soft text-status-error border-t border-status-error/20 flex items-start gap-2"
        >
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
          <span className="flex-1">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="p-0.5 rounded hover:bg-status-error/10"
            aria-label={t("Cerrar")}
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        </div>
      )}

      {/* ── Footer / Composer ─────────────────────────────────────────────── */}
      {isResolved ? (
        <div className="px-[16px] py-[12px] border-t border-border/60 bg-bg-muted/40 text-[13px] text-fg-muted flex items-center gap-2">
          <Lock className="w-4 h-4" aria-hidden="true" />
          {t("Conversación cerrada")}
        </div>
      ) : (
        // Second visual pass: the composer is ONE card floating on the thread
        // surface (mode tabs, copilot, input and tools inside), not a stack of
        // full-width bands. Note mode tints the whole card amber in light and
        // outlines it amber on a neutral surface in dark.
        <div className="shrink-0 px-[12px] pt-[4px] pb-[12px] bg-bg-muted/40">
        <div
          className={cn(
            "rounded-xl border bg-bg shadow-soft transition-colors motion-reduce:transition-none",
            // Focus lives on the card (the textarea is borderless): a 2px
            // neutral outline (fg-muted, >= 3:1 on both themes) so keyboard
            // focus is unmistakable without adding a second accent.
            "focus-within:ring-1",
            supportsWorkflow && composerMode === "note"
              ? "border-status-warning/45 bg-status-warning-soft/40 dark:bg-bg focus-within:border-status-warning focus-within:ring-status-warning"
              : "border-border focus-within:border-fg-muted focus-within:ring-fg-muted",
          )}
        >
          {/* Barra del composer (solo canales con herramientas de trabajo):
              pestañas Responder / Nota privada, copiloto y su panel. */}
          {supportsWorkflow && (
            <div>
              <CopilotPanel
                summary={copilot.summary}
                error={copilot.error}
                onDismiss={dismissCopilot}
              />
              <div className="px-[8px] pt-[6px] flex items-center gap-2">
                <ComposerModeTabs
                  mode={composerMode}
                  onChange={changeComposerMode}
                  noteDisabledReason={
                    attachedFile || voiceActive
                      ? t("Quita el adjunto o la nota de voz para escribir una nota privada")
                      : null
                  }
                />
                <span className="flex-1" aria-hidden="true" />
                <CopilotMenu
                  loading={copilot.loading}
                  onSummary={handleCopilotSummary}
                  onReply={handleCopilotReply}
                  replyDisabledReason={
                    canCompose ? null : t("Toma el caso para poder responder")
                  }
                />
              </div>
            </div>
          )}
          {/* La nota se oculta (no se desmonta) al volver a "Responder". */}
          {supportsWorkflow && noteMounted && (
            <div hidden={composerMode !== "note"}>
              <PrivateNoteComposer
                key={conversation.id}
                conversationId={conversation.id}
                autoFocus
              />
            </div>
          )}
          {supportsWorkflow && composerMode === "note" ? null : isPending && !isAIChannel ? (
        <div className="px-[12px] pt-[8px] pb-[12px] text-[13px] text-fg-muted flex items-center gap-2">
          <Info className="w-4 h-4 shrink-0" aria-hidden="true" />
          {t("Toma el caso para poder responder")}
        </div>
      ) : (
        // canCompose es true para AI (cuando no está cerrada/pending)
        <div>
          {routeNotice && (
            <div
              role="note"
              className="mx-[8px] mt-[8px] px-[10px] py-[6px] rounded-lg text-[12px] bg-status-warning-soft text-status-warning flex items-start gap-2"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span className="flex-1">{routeNotice.text}</span>
            </div>
          )}
          {attachedFile && (
            <div className="px-[10px] pt-[10px]">
              <div className="relative inline-flex max-w-full items-center gap-2 p-2 pr-8 rounded-lg border border-border bg-bg-muted/50">
                {attachedFile.type.startsWith("image/") && filePreview ? (
                  <img
                    src={filePreview}
                    alt={attachedFile.name}
                    className="w-10 h-10 rounded object-cover dark-safe-img"
                  />
                ) : attachedFile.type.startsWith("audio/") && filePreview ? (
                  <audio controls src={filePreview} preload="metadata" className="h-8" />
                ) : attachedFile.type.startsWith("video/") && filePreview ? (
                  <video controls src={filePreview} preload="metadata" className="h-16 rounded" />
                ) : (
                  <IconForFile mime={attachedFile.type} className="w-5 h-5 text-fg-muted" />
                )}
                <div className="min-w-0">
                  <div className="text-xs font-medium text-fg truncate max-w-[200px]">
                    {attachedFile.name}
                  </div>
                  <div className="text-[11px] text-fg-muted">
                    {(attachedFile.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  aria-label={t("Quitar archivo adjunto")}
                  className="absolute top-1 right-1 inline-flex w-5 h-5 items-center justify-center rounded-full bg-bg border border-border text-fg-muted hover:text-fg"
                >
                  <X className="w-3 h-3" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {aiDraft && (
            <div className="mx-[8px] mt-[8px] px-[10px] py-[6px] rounded-lg bg-bg-subtle text-fg text-[12px] flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 shrink-0 text-brand-700" aria-hidden="true" />
              <span className="flex-1">
                {t("Borrador sugerido por la IA: revísalo y edítalo antes de enviar.")}
              </span>
              <button
                type="button"
                onClick={() => setAiDraft(false)}
                className="p-0.5 rounded text-fg-muted hover:bg-bg-muted hover:text-fg"
                aria-label={t("Quitar la marca de borrador")}
              >
                <X className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            </div>
          )}
          <div className="px-[6px] pt-[4px] pb-[6px] flex items-end gap-[2px]">
            {voiceActive ? (
              <VoiceRecorder
                onSend={sendVoiceNote}
                onCancel={() => setVoiceActive(false)}
                sending={sending}
              />
            ) : (
              <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,audio/*,video/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/zip,application/x-rar-compressed,application/x-7z-compressed,text/plain,text/csv"
              className="hidden"
              onChange={handleFileSelect}
              aria-hidden="true"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              aria-label={t("Adjuntar archivo")}
              title={t("Adjuntar archivo")}
              /* A6 · 44px táctiles como el resto del composer (enviar y nota
                 de voz ya lo eran): la fila mezclaba objetivos de 44 y de 36. */
              className="inline-flex w-11 h-11 md:w-9 md:h-9 items-center justify-center rounded-md text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-50 transition-colors shrink-0"
            >
              <Paperclip className="w-4 h-4" aria-hidden="true" />
            </button>
            <div className="relative shrink-0">
              <QuickReplyPicker
                open={quickOpen}
                onClose={closeQuick}
                onSelect={insertAtCaret}
                channel={conversation?.channel}
                anchorRef={quickBtnRef}
              />
              <button
                ref={quickBtnRef}
                type="button"
                onClick={() => {
                  slashOpenedRef.current = false;
                  setQuickOpen((p) => !p);
                }}
                disabled={sending}
                aria-haspopup="dialog"
                aria-expanded={quickOpen}
                aria-label={t("Respuestas rápidas")}
                title={t("Respuestas rápidas (/)")}
                className={cn(
                  "inline-flex w-11 h-11 md:w-9 md:h-9 items-center justify-center rounded-md disabled:opacity-50 transition-colors",
                  quickOpen
                    ? "bg-bg-subtle text-fg"
                    : "text-fg-muted hover:bg-bg-muted hover:text-fg",
                )}
              >
                <Zap className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onFocus={() => setComposerFocused(true)}
              onBlur={() => setComposerFocused(false)}
              placeholder={
                attachedFile
                  ? t("Escribe una descripción (opcional)…")
                  : isAIChannel
                    ? t("Responde al usuario (interviene a la IA)…")
                    : t("Escribe una respuesta al usuario…")
              }
              rows={1}
              disabled={sending}
              aria-label={t("Respuesta al usuario")}
              /* El placeholder envolvía a dos líneas en la columna central
                 estrecha y la segunda quedaba cortada por el min-h de una fila.
                 nowrap + ellipsis solo afecta al placeholder: el texto que
                 escribe el agente sigue envolviendo y creciendo hasta max-h. */
              /* Borderless inside the card: the card carries the border and
                 the focus state (focus-within). */
              className="flex-1 min-h-[40px] max-h-40 px-[8px] py-[10px] rounded-md border-0 bg-transparent focus:bg-transparent text-[16px] md:text-[14px] leading-[20px] text-fg placeholder:text-fg-muted placeholder:whitespace-nowrap placeholder:overflow-hidden placeholder:text-ellipsis resize-none focus:outline-none focus:ring-0 focus:shadow-none disabled:opacity-50"
            />
            {input.trim() || attachedFile ? (
              <button
                type="button"
                onClick={handleSend}
                disabled={(!input.trim() && !attachedFile) || sending}
                aria-label={t("Enviar mensaje")}
                title={t("Enviar (Ctrl/Cmd+Enter)")}
                className="hd-brand-solid inline-flex w-11 h-11 md:w-9 md:h-9 items-center justify-center rounded-lg bg-brand-500 text-bg hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                {sending ? (
                  <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setVoiceActive(true);
                }}
                disabled={sending}
                aria-label={t("Grabar nota de voz")}
                title={t("Grabar nota de voz")}
                /* Ghost: the accent is reserved for Send (with content). An
                   always-orange mic read as the primary action of an empty
                   composer. */
                className="inline-flex w-11 h-11 md:w-9 md:h-9 items-center justify-center rounded-lg text-fg-muted hover:bg-bg-muted hover:text-fg disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                <Mic className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
              </>
            )}
          </div>
          {/* Atajos de teclado físico: irrelevantes en táctil, se omiten en mobile.
              Solo visibles con el composer enfocado, para que en reposo no compitan
              con el hilo — pero el bloque se monta siempre y solo cambia de opacidad:
              montarlo y desmontarlo alteraba el alto del composer en cada focus/blur
              y la lista de mensajes daba un salto. */}
          {!isMobile && (
            <div
              aria-hidden={!composerFocused}
              className={cn(
                "px-[14px] pb-[8px] -mt-[2px] min-h-[1.25rem] text-[11px] text-fg-muted transition-opacity duration-150 ease-out motion-reduce:transition-none",
                composerFocused ? "opacity-100" : "opacity-0 pointer-events-none",
              )}
            >
              {t("Enter envía · Shift+Enter nueva línea · / respuestas rápidas")}
            </div>
          )}
        </div>
          )}
        </div>
        </div>
      )}

      {lightbox && (
        <MediaLightbox
          url={lightbox.url}
          caption={lightbox.caption}
          profile={lightbox.profile || false}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Renombrar grupo — solo conversaciones is_group. Estado del borrador
          ENCAPSULADO en el modal: teclear ahí no re-renderiza el hilo. */}
      <RenameGroupModal
        open={renameOpen}
        conversation={conversation}
        currentName={currentGroupName}
        onClose={closeRename}
        onUpdated={onUpdated}
      />

      <ConfirmResolveModal
        open={showConfirmResolve}
        onCancel={() => setShowConfirmResolve(false)}
        onConfirm={handleResolve}
        loading={actionLoading}
      />

      {sidePanelOpen && (
        <ContactPanel
          conversation={conversation}
          onConversationUpdate={(updated) => onUpdated?.(updated)}
          onClose={() => setSidePanelOpen(false)}
          mode="overlay"
        />
      )}
    </div>
  );
}
